import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { PendingUsersBanner } from '../../components/PendingUsersBanner';
import { useMobileDetect } from '../../hooks/useMobileDetect';
import {
  Project,
  ProjectUpdate,
  Room,
  Story,
  DocumentSet,
  Document,
  DocumentVersionResponse,
  Trade,
  ActivityLog as ActivityLogType,
  NoteWithDetails,
  Attachment,
  Schedule,
  getProjects,
  getRooms,
  getStories,
  createRoom,
  deleteRoom,
  getDocumentSets,
  createDocumentSet,
  uploadDocument,
  getDocuments,
  getDocumentVersions,
  renameDocument,
  deleteDocument,
  getTradesAuth,
  createTrade,
  deleteTrade,
  updateTrade,
  startConversion,
  getConversionStatus,
  getProjectActivity,
  getProjectNotes,
  deleteNote,
  updateNote,
  updateNoteRooms,
  createMultiRoomNote,
  uploadNotePhoto,
  updateProject,
  deleteProject,
  getNotePhotoUrl,
  getDocumentOCRSummary,
  OCRSummaryResponse,
  getProjectAttachments,
  uploadAttachments,
  updateAttachment,
  deleteAttachment,
  setAttachmentRooms,
  getAttachmentDownloadUrl,
  getProjectSchedules,
  deleteSchedule,
  extractSchedule,
} from '../../api/client';
import { PhotoLightbox } from '../../components/PhotoLightbox';
import { ScheduleUploadModal } from '../../components/ScheduleUploadModal';
import { ScheduleViewer } from '../../components/ScheduleViewer';

type TabType = 'rooms' | 'documents' | 'schedules' | 'trades' | 'activity' | 'notes' | 'settings';

interface RoomChipProps {
  room: { room_id: number; room_number: string; room_name: string | null };
  onRemove?: () => void;
}

interface RoomSelectorProps {
  rooms: Room[];
  selectedRoomIds: number[];
  searchInput: string;
  setSearchInput: (v: string) => void;
  showDropdown: boolean;
  setShowDropdown: (v: boolean) => void;
  onAddRoom: (id: number) => void;
  onRemoveRoom: (id: number) => void;
  getFilteredRoomsForSearch: (searchInput: string, excludeIds: number[]) => Room[];
}

interface OCRWarningsSectionProps {
  documentId: number;
  warningsJson: string | null;
  conversionStatus: 'pending' | 'converting' | 'ready' | 'failed';
}

const OCRWarningsSection: React.FC<OCRWarningsSectionProps> = ({ documentId, warningsJson, conversionStatus }) => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [summary, setSummary] = useState<OCRSummaryResponse | null>(null);

  useEffect(() => {
    if (!isExpanded || !token) return;
    setIsLoading(true);
    getDocumentOCRSummary(token, documentId)
      .then(setSummary)
      .catch(() => setSummary(null))
      .finally(() => setIsLoading(false));
  }, [isExpanded, token, documentId]);

  const warnings = summary?.has_warnings || (warningsJson ? JSON.parse(warningsJson).has_warnings : false);
  const pagesProcessed = summary?.pages_processed ?? 0;
  const totalPages = summary?.mappings_created ? summary.mappings_created + (summary.unmapped_pages?.length || 0) : 0;

  if (conversionStatus !== 'ready') return null;

  if (!warnings && pagesProcessed > 0) {
    return (
      <div style={{ marginTop: '8px', fontSize: '13px', color: '#28a745' }}>
        ✓ Auto-mapped {summary?.mappings_created ?? '?'}/{pagesProcessed} pages
      </div>
    );
  }

  return (
    <div style={{ marginTop: '8px' }}>
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        style={{
          background: '#ffc107',
          border: 'none',
          borderRadius: '4px',
          padding: '4px 8px',
          fontSize: '12px',
          cursor: 'pointer',
          color: '#333'
        }}
      >
        ⚠️ {isExpanded ? 'Hide' : 'Show'} OCR Issues ({(summary?.unrecognized_rooms?.length || 0) + (summary?.unmapped_pages?.length || 0)})
      </button>
      {isExpanded && (
        <div style={{ marginTop: '8px', padding: '12px', background: '#fffbeb', borderRadius: '4px', fontSize: '13px' }}>
          {isLoading ? (
            <div>Loading OCR summary...</div>
          ) : summary ? (
            <>
              <div style={{ marginBottom: '8px' }}>
                <strong>Pages processed:</strong> {summary.pages_processed} | <strong>Mappings created:</strong> {summary.mappings_created}
              </div>
              {summary.unrecognized_rooms.length > 0 && (
                <div style={{ marginBottom: '8px' }}>
                  <strong>Unrecognized rooms:</strong>
                  <div style={{ marginTop: '4px' }}>
                    {summary.unrecognized_rooms.map((r, i) => (
                      <span key={i} style={{ display: 'inline-block', background: '#fee', padding: '2px 6px', borderRadius: '4px', margin: '2px', fontSize: '12px' }}>
                        {r.room_number} (page {r.page})
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {summary.unmapped_pages.length > 0 && (
                <div style={{ marginBottom: '8px' }}>
                  <strong>Unmapped pages:</strong>
                  <div style={{ marginTop: '4px' }}>
                    {summary.unmapped_pages.map(p => (
                      <span key={p} style={{ display: 'inline-block', background: '#eee', padding: '2px 6px', borderRadius: '4px', margin: '2px', fontSize: '12px' }}>
                        Page {p}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {summary.errors.length > 0 && (
                <div style={{ marginBottom: '8px', color: '#dc3545' }}>
                  <strong>Errors:</strong>
                  <ul style={{ margin: '4px 0', paddingLeft: '16px' }}>
                    {summary.errors.map((e, i) => <li key={i}>{e}</li>)}
                  </ul>
                </div>
              )}
              <button
                onClick={() => navigate(`/admin/projects/${summary.document_id}/documents/${documentId}/map`)}
                style={{
                  background: '#007bff',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  padding: '6px 12px',
                  fontSize: '13px',
                  cursor: 'pointer',
                  marginTop: '8px'
                }}
              >
                Open in Room Mapper
              </button>
            </>
          ) : (
            <div>Failed to load OCR summary</div>
          )}
        </div>
      )}
    </div>
  );
};

const RoomChip: React.FC<RoomChipProps> = ({ room, onRemove }) => (
  <span style={{
    display: 'inline-flex',
    alignItems: 'center',
    background: '#e9ecef',
    padding: '4px 8px',
    borderRadius: '12px',
    fontSize: '12px',
    marginRight: '4px',
    marginBottom: '4px'
  }}>
    {room.room_number}{room.room_name ? ` - ${room.room_name}` : ''}
    {onRemove && (
      <button
        onClick={onRemove}
        style={{
          background: 'none',
          border: 'none',
          marginLeft: '4px',
          cursor: 'pointer',
          fontSize: '14px',
          color: '#666',
          padding: '0 2px'
        }}
      >
        x
      </button>
    )}
  </span>
);

const RoomSelector: React.FC<RoomSelectorProps> = ({
  rooms,
  selectedRoomIds,
  searchInput,
  setSearchInput,
  showDropdown,
  setShowDropdown,
  onAddRoom,
  onRemoveRoom,
  getFilteredRoomsForSearch
}) => {
  const filteredRooms = getFilteredRoomsForSearch(searchInput, selectedRoomIds);
  const selectedRooms = rooms.filter(r => selectedRoomIds.includes(r.id));

  return (
    <div>
      <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold', fontSize: '14px' }}>
        Rooms
      </label>
      <div style={{ marginBottom: '8px', display: 'flex', flexWrap: 'wrap' }}>
        {selectedRooms.map(room => (
          <RoomChip
            key={room.id}
            room={{ room_id: room.id, room_number: room.number, room_name: room.name }}
            onRemove={() => onRemoveRoom(room.id)}
          />
        ))}
      </div>
      <div style={{ position: 'relative' }}>
        <input
          type="text"
          value={searchInput}
          onChange={(e) => {
            setSearchInput(e.target.value);
            setShowDropdown(true);
          }}
          onFocus={() => setShowDropdown(true)}
          placeholder="Type room number or name to add..."
          style={{ width: '100%', padding: '8px', fontSize: '14px', boxSizing: 'border-box' }}
        />
        {showDropdown && filteredRooms.length > 0 && (
          <div style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            background: '#fff',
            border: '1px solid #ddd',
            borderRadius: '4px',
            maxHeight: '200px',
            overflowY: 'auto',
            zIndex: 10
          }}>
            {filteredRooms.map(room => (
              <div
                key={room.id}
                onClick={() => onAddRoom(room.id)}
                style={{
                  padding: '8px 12px',
                  cursor: 'pointer',
                  borderBottom: '1px solid #eee'
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = '#f5f5f5')}
                onMouseLeave={(e) => (e.currentTarget.style.background = '#fff')}
              >
                {room.number}{room.name ? ` - ${room.name}` : ''}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export function ProjectDetail() {
  const { projectId } = useParams<{ projectId: string }>();
  const { token, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [project, setProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<TabType>('documents');
  const isMobile = useMobileDetect();
  const [menuOpen, setMenuOpen] = useState(false);
  const [showScheduleUpload, setShowScheduleUpload] = useState(false);
  const [scheduleRefreshKey, setScheduleRefreshKey] = useState(0);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/admin/login');
      return;
    }

    const loadProject = async () => {
      if (!token || !projectId) return;
      try {
        const projects = await getProjects(token);
        const found = projects.find(p => p.id === parseInt(projectId));
        if (found) {
          setProject(found);
        } else {
          console.error('Project not found');
          navigate('/admin');
        }
      } catch (error) {
        console.error('Failed to load project:', error);
        navigate('/admin');
      } finally {
        setLoading(false);
      }
    };

    loadProject();
  }, [projectId, token, isAuthenticated, navigate]);

  if (!isAuthenticated) {
    return null;
  }

  if (loading) {
    return (
      <div style={{ fontFamily: 'system-ui', padding: '24px' }}>
        Loading project...
      </div>
    );
  }

  if (!project) {
    return null;
  }

  return (
    <div style={{ fontFamily: 'system-ui' }}>
      <header style={{
        padding: '16px 24px',
        background: '#1a1a2e',
        color: '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '16px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <button
            onClick={() => navigate('/admin')}
            style={{
              background: 'transparent',
              border: '1px solid #fff',
              color: '#fff',
              padding: '8px 16px',
              cursor: 'pointer',
              borderRadius: '4px',
              fontSize: '14px'
            }}
          >
            ← Back
          </button>
          <div>
            <h1 style={{ margin: 0, fontSize: '20px' }}>{project.name}</h1>
            {project.address && (
              <p style={{ margin: '4px 0 0 0', fontSize: '14px', opacity: 0.8 }}>
                {project.address}
              </p>
            )}
          </div>
        </div>
        {isMobile && (
          <div style={{ position: 'relative' }}>
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              style={{
                background: 'transparent',
                border: '1px solid #fff',
                color: '#fff',
                padding: '8px 12px',
                cursor: 'pointer',
                borderRadius: '4px',
                fontSize: '20px',
                lineHeight: 1
              }}
            >
              &#9776;
            </button>
            {menuOpen && (
              <div style={{
                position: 'absolute',
                top: '100%',
                right: 0,
                marginTop: '8px',
                background: '#fff',
                borderRadius: '8px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                overflow: 'hidden',
                zIndex: 1000,
                minWidth: '180px'
              }}>
                <button
                  onClick={() => { setActiveTab('documents'); setMenuOpen(false); }}
                  style={{
                    display: 'block',
                    width: '100%',
                    background: activeTab === 'documents' ? '#f0f7ff' : 'transparent',
                    border: 'none',
                    padding: '10px 14px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    textAlign: 'left',
                    color: activeTab === 'documents' ? '#007bff' : '#666',
                    fontWeight: activeTab === 'documents' ? 'bold' : 'normal'
                  }}
                >
                  Documents
                </button>
                <button
                  onClick={() => { setActiveTab('schedules'); setMenuOpen(false); }}
                  style={{
                    display: 'block',
                    width: '100%',
                    background: activeTab === 'schedules' ? '#f0f7ff' : 'transparent',
                    border: 'none',
                    borderTop: '1px solid #eee',
                    padding: '10px 14px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    textAlign: 'left',
                    color: activeTab === 'schedules' ? '#007bff' : '#666',
                    fontWeight: activeTab === 'schedules' ? 'bold' : 'normal'
                  }}
                >
                  Schedules
                </button>
                <button
                  onClick={() => { setActiveTab('rooms'); setMenuOpen(false); }}
                  style={{
                    display: 'block',
                    width: '100%',
                    background: activeTab === 'rooms' ? '#f0f7ff' : 'transparent',
                    border: 'none',
                    borderTop: '1px solid #eee',
                    padding: '10px 14px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    textAlign: 'left',
                    color: activeTab === 'rooms' ? '#007bff' : '#666',
                    fontWeight: activeTab === 'rooms' ? 'bold' : 'normal'
                  }}
                >
                  Rooms
                </button>
                <button
                  onClick={() => { setActiveTab('notes'); setMenuOpen(false); }}
                  style={{
                    display: 'block',
                    width: '100%',
                    background: activeTab === 'notes' ? '#f0f7ff' : 'transparent',
                    border: 'none',
                    borderTop: '1px solid #eee',
                    padding: '10px 14px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    textAlign: 'left',
                    color: activeTab === 'notes' ? '#007bff' : '#666',
                    fontWeight: activeTab === 'notes' ? 'bold' : 'normal'
                  }}
                >
                  Notes
                </button>
                <button
                  onClick={() => { navigate(`/admin/projects/${projectId}/tasks`); setMenuOpen(false); }}
                  style={{
                    display: 'block',
                    width: '100%',
                    background: 'transparent',
                    border: 'none',
                    borderTop: '1px solid #eee',
                    padding: '10px 14px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    textAlign: 'left',
                    color: '#666',
                    fontWeight: 'normal'
                  }}
                >
                  Work-to-Complete
                </button>
                <button
                  onClick={() => { setActiveTab('activity'); setMenuOpen(false); }}
                  style={{
                    display: 'block',
                    width: '100%',
                    background: activeTab === 'activity' ? '#f0f7ff' : 'transparent',
                    border: 'none',
                    borderTop: '1px solid #eee',
                    padding: '10px 14px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    textAlign: 'left',
                    color: activeTab === 'activity' ? '#007bff' : '#666',
                    fontWeight: activeTab === 'activity' ? 'bold' : 'normal'
                  }}
                >
                  Activity
                </button>
                <button
                  onClick={() => { setActiveTab('settings'); setMenuOpen(false); }}
                  style={{
                    display: 'block',
                    width: '100%',
                    background: activeTab === 'settings' ? '#f0f7ff' : 'transparent',
                    border: 'none',
                    borderTop: '1px solid #eee',
                    padding: '10px 14px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    textAlign: 'left',
                    color: activeTab === 'settings' ? '#007bff' : '#666',
                    fontWeight: activeTab === 'settings' ? 'bold' : 'normal'
                  }}
                >
                  Settings
                </button>
              </div>
            )}
          </div>
        )}
      </header>

      <PendingUsersBanner />

      {!isMobile && (
        <div style={{
          borderBottom: '1px solid #ddd',
          background: '#f8f9fa'
        }}>
          <div style={{
            maxWidth: '1200px',
            margin: '0 auto',
            display: 'flex',
            gap: '0'
          }}>
            <button
              onClick={() => setActiveTab('documents')}
              style={{
                background: activeTab === 'documents' ? '#fff' : 'transparent',
                border: 'none',
                borderBottom: activeTab === 'documents' ? '3px solid #007bff' : '3px solid transparent',
                padding: '16px 24px',
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: activeTab === 'documents' ? 'bold' : 'normal',
                color: activeTab === 'documents' ? '#007bff' : '#666'
              }}
            >
              Documents
            </button>
            <button
              onClick={() => setActiveTab('schedules')}
              style={{
                background: activeTab === 'schedules' ? '#fff' : 'transparent',
                border: 'none',
                borderBottom: activeTab === 'schedules' ? '3px solid #007bff' : '3px solid transparent',
                padding: '16px 24px',
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: activeTab === 'schedules' ? 'bold' : 'normal',
                color: activeTab === 'schedules' ? '#007bff' : '#666'
              }}
            >
              Schedules
            </button>
            <button
              onClick={() => setActiveTab('rooms')}
              style={{
                background: activeTab === 'rooms' ? '#fff' : 'transparent',
                border: 'none',
                borderBottom: activeTab === 'rooms' ? '3px solid #007bff' : '3px solid transparent',
                padding: '16px 24px',
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: activeTab === 'rooms' ? 'bold' : 'normal',
                color: activeTab === 'rooms' ? '#007bff' : '#666'
              }}
            >
              Rooms
            </button>
            <button
              onClick={() => setActiveTab('notes')}
              style={{
                background: activeTab === 'notes' ? '#fff' : 'transparent',
                border: 'none',
                borderBottom: activeTab === 'notes' ? '3px solid #007bff' : '3px solid transparent',
                padding: '16px 24px',
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: activeTab === 'notes' ? 'bold' : 'normal',
                color: activeTab === 'notes' ? '#007bff' : '#666'
              }}
            >
              Notes
            </button>
            <button
              onClick={() => navigate(`/admin/projects/${projectId}/tasks`)}
              style={{
                background: 'transparent',
                border: 'none',
                borderBottom: '3px solid transparent',
                padding: '16px 24px',
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: 'normal',
                color: '#666'
              }}
            >
              Work-to-Complete
            </button>
            <button
              onClick={() => setActiveTab('activity')}
              style={{
                background: activeTab === 'activity' ? '#fff' : 'transparent',
                border: 'none',
                borderBottom: activeTab === 'activity' ? '3px solid #007bff' : '3px solid transparent',
                padding: '16px 24px',
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: activeTab === 'activity' ? 'bold' : 'normal',
                color: activeTab === 'activity' ? '#007bff' : '#666'
              }}
            >
              Activity
            </button>
            <button
              onClick={() => setActiveTab('settings')}
              style={{
                background: activeTab === 'settings' ? '#fff' : 'transparent',
                border: 'none',
                borderBottom: activeTab === 'settings' ? '3px solid #007bff' : '3px solid transparent',
                padding: '16px 24px',
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: activeTab === 'settings' ? 'bold' : 'normal',
                color: activeTab === 'settings' ? '#007bff' : '#666'
              }}
            >
              Settings
            </button>
          </div>
        </div>
      )}

      <main style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
        {activeTab === 'rooms' && <RoomsTab projectId={parseInt(projectId!)} />}
        {activeTab === 'documents' && <DocumentsTab projectId={parseInt(projectId!)} />}
        {activeTab === 'schedules' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <h2 style={{ margin: 0 }}>Schedules</h2>
              <button
                onClick={() => setShowScheduleUpload(true)}
                style={{
                  background: '#1a1a2e',
                  color: '#fff',
                  border: 'none',
                  padding: '10px 20px',
                  cursor: 'pointer',
                  borderRadius: '4px'
                }}
              >
                + Upload Schedule
              </button>
            </div>
            <ScheduleViewer projectId={parseInt(projectId!)} token={token || ''} refreshTrigger={scheduleRefreshKey} />
          </div>
        )}
        {activeTab === 'trades' && <TradesTab projectId={parseInt(projectId!)} />}
        {activeTab === 'activity' && <ActivityTab projectId={parseInt(projectId!)} />}
        {activeTab === 'notes' && <NotesTab projectId={parseInt(projectId!)} />}
        {activeTab === 'settings' && <SettingsTab projectId={parseInt(projectId!)} project={project} onProjectUpdated={setProject} />}
      </main>

      {showScheduleUpload && (
        <ScheduleUploadModal
          projectId={parseInt(projectId!)}
          token={token || ''}
          onUpload={() => {
            setShowScheduleUpload(false);
            setScheduleRefreshKey(k => k + 1);
          }}
          onClose={() => setShowScheduleUpload(false)}
        />
      )}
    </div>
  );
}

function RoomsTab({ projectId }: { projectId: number }) {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [rooms, setRooms] = useState<Room[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [newNumber, setNewNumber] = useState('');
  const [newName, setNewName] = useState('');
  const [isExterior, setIsExterior] = useState(false);

  const loadRooms = async () => {
    if (!token) return;
    try {
      const data = await getRooms(token, projectId);
      setRooms(data);
    } catch (error) {
      console.error('Failed to load rooms:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRooms();
  }, [token, projectId]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token || !newNumber.trim()) return;
    try {
      await createRoom(token, projectId, newNumber, newName || undefined, isExterior);
      setNewNumber('');
      setNewName('');
      setIsExterior(false);
      setShowForm(false);
      loadRooms();
    } catch (error) {
      console.error('Failed to create room:', error);
    }
  };

  const displayNumber = isExterior ? `EXT-${newNumber}` : newNumber;

  const handleNumberChange = (value: string) => {
    const trimmedStart = value.trimStart();
    const hasExteriorPrefix = /^ext-/i.test(trimmedStart);
    const stripped = hasExteriorPrefix
      ? trimmedStart.replace(/^ext-/i, '').trimStart()
      : trimmedStart;

    setIsExterior(hasExteriorPrefix);
    setNewNumber(stripped);
  };

  const handleDelete = async (roomId: number) => {
    if (!token) return;
    if (!window.confirm('Are you sure you want to delete this room?')) return;
    try {
      await deleteRoom(token, projectId, roomId);
      loadRooms();
    } catch (error) {
      console.error('Failed to delete room:', error);
    }
  };

  // Group rooms by floor (hundreds digit) with exterior grouping
  const groupedRooms = rooms.reduce((acc, room) => {
    if (room.is_exterior) {
      if (!acc['Exterior']) acc['Exterior'] = [];
      acc['Exterior'].push(room);
      return acc;
    }

    const roomNum = parseInt(room.number);
    if (isNaN(roomNum)) {
      // Handle non-numeric room numbers
      if (!acc['Other']) acc['Other'] = [];
      acc['Other'].push(room);
    } else {
      const floor = Math.floor(roomNum / 100);
      const floorKey = `Floor ${floor}`;
      if (!acc[floorKey]) acc[floorKey] = [];
      acc[floorKey].push(room);
    }
    return acc;
  }, {} as Record<string, Room[]>);

  // Sort floor keys
  const sortedFloorKeys = Object.keys(groupedRooms).sort((a, b) => {
    if (a === 'Exterior') return 1;
    if (b === 'Exterior') return -1;
    if (a === 'Other') return 1;
    if (b === 'Other') return -1;
    const floorA = parseInt(a.replace('Floor ', ''));
    const floorB = parseInt(b.replace('Floor ', ''));
    return floorA - floorB;
  });

  if (loading) {
    return <div>Loading rooms...</div>;
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <h2 style={{ margin: 0 }}>Rooms</h2>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            onClick={() => navigate(`/admin/projects/${projectId}/qr-codes`)}
            style={{
              background: '#007bff',
              color: '#fff',
              border: 'none',
              padding: '10px 20px',
              cursor: 'pointer',
              borderRadius: '4px'
            }}
          >
            Print QR Labels
          </button>
          <button
            onClick={() => setShowForm(!showForm)}
            style={{
              background: '#1a1a2e',
              color: '#fff',
              border: 'none',
              padding: '10px 20px',
              cursor: 'pointer',
              borderRadius: '4px'
            }}
          >
            {showForm ? 'Cancel' : '+ New Room'}
          </button>
        </div>
      </div>

      {showForm && (
        <form onSubmit={handleCreate} style={{
          background: '#f5f5f5',
          padding: '20px',
          marginBottom: '24px',
          borderRadius: '8px'
        }}>
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold' }}>Room Number *</label>
            <input
              type="text"
              value={displayNumber}
              onChange={(e) => handleNumberChange(e.target.value)}
              placeholder="e.g., 101"
              style={{ width: '100%', padding: '10px', boxSizing: 'border-box' }}
              required
            />
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '8px' }}>
              <input
                type="checkbox"
                checked={isExterior}
                onChange={(e) => setIsExterior(e.target.checked)}
              />
              Exterior (adds EXT- prefix)
            </label>
          </div>
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold' }}>Room Name</label>
            <input
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="e.g., Master Bedroom"
              style={{ width: '100%', padding: '10px', boxSizing: 'border-box' }}
            />
          </div>
          <button
            type="submit"
            style={{
              background: '#28a745',
              color: '#fff',
              border: 'none',
              padding: '10px 20px',
              cursor: 'pointer',
              borderRadius: '4px'
            }}
          >
            Create Room
          </button>
        </form>
      )}

      {rooms.length === 0 ? (
        <p style={{ color: '#666' }}>No rooms yet. Create your first room!</p>
      ) : (
        <div>
          {sortedFloorKeys.map(floorKey => (
            <div key={floorKey} style={{ marginBottom: '32px' }}>
              <h3 style={{
                margin: '0 0 16px 0',
                paddingBottom: '8px',
                borderBottom: '2px solid #ddd',
                color: '#333'
              }}>
                {floorKey}
              </h3>
              <div style={{ display: 'grid', gap: '12px' }}>
                {groupedRooms[floorKey]
                  .sort((a, b) => a.number.localeCompare(b.number, undefined, { numeric: true }))
                  .map(room => (
                    <div
                      key={room.id}
                      onClick={() => navigate(`/admin/projects/${projectId}/rooms/${room.id}`)}
                      style={{
                        border: '1px solid #ddd',
                        borderRadius: '8px',
                        padding: '16px',
                        paddingTop: '12px',
                        paddingRight: '36px',
                        position: 'relative',
                        background: '#fff',
                        cursor: 'pointer',
                        transition: 'background-color 0.15s, border-color 0.15s'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#f8f9fa';
                        e.currentTarget.style.borderColor = '#007bff';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = '#fff';
                        e.currentTarget.style.borderColor = '#ddd';
                      }}
                    >
                      {/* × Delete Icon - top right corner */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(room.id);
                        }}
                        style={{
                          position: 'absolute',
                          top: '8px',
                          right: '12px',
                          background: 'transparent',
                          border: 'none',
                          padding: '4px 8px',
                          cursor: 'pointer',
                          fontSize: '20px',
                          lineHeight: '1',
                          color: '#999',
                          transition: 'color 0.15s'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.color = '#dc3545';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.color = '#999';
                        }}
                        title="Delete room"
                      >
                        ×
                      </button>

                      <div>
                        <h4 style={{ margin: '0 0 4px 0', fontSize: '16px' }}>
                          Room {room.is_exterior ? `EXT-${room.number}` : room.number}
                        </h4>
                        {room.name && (
                          <p style={{ margin: 0, color: '#666', fontSize: '14px' }}>
                            {room.name}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function DocumentsTab({ projectId }: { projectId: number }) {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [documentSets, setDocumentSets] = useState<DocumentSet[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [newSetName, setNewSetName] = useState('');
  const [expandedSets, setExpandedSets] = useState<Set<number>>(new Set());
  const [documents, setDocuments] = useState<Record<number, Document[]>>({});
  const [uploading, setUploading] = useState<number | null>(null);
  const [uploadProgress, setUploadProgress] = useState<Record<number, number>>({});
  const [converting, setConverting] = useState<Set<number>>(new Set());
  const [conversionProgress, setConversionProgress] = useState<Record<number, number>>({});
  const [editingDocId, setEditingDocId] = useState<number | null>(null);
  const [editingDocName, setEditingDocName] = useState('');
  const [expandedDocuments, setExpandedDocuments] = useState<Set<number>>(new Set());
  const [documentVersions, setDocumentVersions] = useState<Record<number, DocumentVersionResponse[]>>({});
  const [loadingVersions, setLoadingVersions] = useState<Set<number>>(new Set());

  // Attachments state
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [attachmentsExpanded, setAttachmentsExpanded] = useState(false);
  const [uploadingAttachments, setUploadingAttachments] = useState(false);
  const [rooms, setRooms] = useState<Room[]>([]);
  const [stories, setStories] = useState<Story[]>([]);
  const [editingAttachmentId, setEditingAttachmentId] = useState<number | null>(null);
  const [editingAttachmentName, setEditingAttachmentName] = useState('');
  const [attachmentMenuOpen, setAttachmentMenuOpen] = useState<number | null>(null);
  const [roomDropdownAttachmentId, setRoomDropdownAttachmentId] = useState<number | null>(null);
  const [roomSearchInput, setRoomSearchInput] = useState('');
  const [roomHighlightIndex, setRoomHighlightIndex] = useState(0);

  // Upload modal state
  const [uploadModal, setUploadModal] = useState<{
    isOpen: boolean;
    setId: number;
    file: File | null;
    step: 'choose' | 'select-doc';
  } | null>(null);

  const loadDocumentSets = async () => {
    if (!token) return;
    try {
      const data = await getDocumentSets(projectId);
      setDocumentSets(data);

      // Load documents for all sets to show correct counts
      const docsMap: Record<number, Document[]> = {};
      await Promise.all(data.map(async (set) => {
        try {
          const docs = await getDocuments(token, set.id, true);
          docsMap[set.id] = docs;
        } catch (error) {
          console.error(`Failed to load documents for set ${set.id}:`, error);
          docsMap[set.id] = [];
        }
      }));
      setDocuments(docsMap);
    } catch (error) {
      console.error('Failed to load document sets:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDocumentSets();
    if (token) {
      getProjectAttachments(token, projectId).then(setAttachments).catch(console.error);
      getRooms(token, projectId).then(setRooms).catch(console.error);
      getStories(token, projectId).then(setStories).catch(console.error);
    }
  }, [token, projectId]);

  const handleCreateSet = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token || !newSetName.trim()) return;
    try {
      await createDocumentSet(token, projectId, newSetName);
      setNewSetName('');
      setShowForm(false);
      loadDocumentSets();
    } catch (error) {
      console.error('Failed to create document set:', error);
    }
  };

  const toggleSet = async (setId: number) => {
    const newExpanded = new Set(expandedSets);
    if (newExpanded.has(setId)) {
      newExpanded.delete(setId);
    } else {
      newExpanded.add(setId);
      // Load documents for this set if not already loaded
      if (!documents[setId] && token) {
        try {
          const docs = await getDocuments(token, setId, true);
          setDocuments(prev => ({ ...prev, [setId]: docs }));
        } catch (error) {
          console.error('Failed to load documents:', error);
        }
      }
    }
    setExpandedSets(newExpanded);
  };

  const handleFileSelect = (setId: number, file: File | null) => {
    if (!token || !file) return;
    if (file.type !== 'application/pdf') {
      alert('Only PDF files are allowed');
      return;
    }

    const setDocs = documents[setId] || [];
    const currentDocs = setDocs.filter(d => d.is_current);

    // If no existing documents, upload directly as new
    if (currentDocs.length === 0) {
      handleUpload(setId, file, undefined);
      return;
    }

    // Show modal to ask addition or replacement
    setUploadModal({
      isOpen: true,
      setId,
      file,
      step: 'choose'
    });
  };

  const handleUploadChoice = (isReplacement: boolean) => {
    if (!uploadModal) return;

    if (!isReplacement) {
      // Addition - upload directly
      handleUpload(uploadModal.setId, uploadModal.file!, undefined);
      setUploadModal(null);
    } else {
      // Replacement - show document selection
      setUploadModal(prev => prev ? { ...prev, step: 'select-doc' } : null);
    }
  };

  const handleSelectReplacement = (docId: number) => {
    if (!uploadModal) return;
    handleUpload(uploadModal.setId, uploadModal.file!, docId);
    setUploadModal(null);
  };

  const handleUpload = async (setId: number, file: File, replaceDocId: number | undefined) => {
    if (!token) return;

    setUploading(setId);
    setUploadProgress(prev => ({ ...prev, [setId]: 0 }));

    try {
      const result = await uploadDocument(
        token,
        setId,
        file,
        replaceDocId,
        (progress) => {
          setUploadProgress(prev => ({ ...prev, [setId]: progress }));
        }
      );
      // Document is now always active, auto-OCR will run after conversion
      // Show message about auto-mapping
      if (result.old_page_count && result.old_page_count !== result.new_page_count) {
        alert(`Document replaced. Auto-OCR will map rooms after conversion. Page count changed from ${result.old_page_count} to ${result.new_page_count}.`);
      }
      // Reload documents for this set
      const docs = await getDocuments(token, setId, true);
      setDocuments(prev => ({ ...prev, [setId]: docs }));
    } catch (error) {
      console.error('Failed to upload document:', error);
      alert('Failed to upload document');
    } finally {
      setUploading(null);
      setUploadProgress(prev => {
        const newProgress = { ...prev };
        delete newProgress[setId];
        return newProgress;
      });
    }
  };

  const handleRenameDocument = async (doc: Document, setId: number) => {
    if (!token || !editingDocName.trim()) return;
    try {
      const updated = await renameDocument(token, doc.id, editingDocName);
      // Update the document in state
      setDocuments(prev => ({
        ...prev,
        [setId]: prev[setId].map(d => d.id === doc.id ? updated : d)
      }));
      setEditingDocId(null);
      setEditingDocName('');
    } catch (error) {
      console.error('Failed to rename document:', error);
      alert('Failed to rename document');
    }
  };

  const startEditing = (doc: Document) => {
    setEditingDocId(doc.id);
    setEditingDocName(doc.filename);
  };

  const cancelEditing = () => {
    setEditingDocId(null);
    setEditingDocName('');
  };

  const handleDeleteDocument = async (doc: Document, setId: number) => {
    if (!token) return;
    if (!window.confirm(`Are you sure you want to delete "${doc.filename}"? This cannot be undone.`)) return;
    try {
      await deleteDocument(token, doc.id);
      // Remove from local state
      setDocuments(prev => ({
        ...prev,
        [setId]: prev[setId].filter(d => d.id !== doc.id)
      }));
    } catch (error) {
      console.error('Failed to delete document:', error);
      alert('Failed to delete document');
    }
  };

  const handleConvertDocument = async (doc: Document, setId: number) => {
    if (!token) return;
    try {
      // Start conversion
      await startConversion(token, doc.id);

      // Add to converting set
      setConverting(prev => new Set(prev).add(doc.id));

      // Start polling for status
      const pollInterval = setInterval(async () => {
        try {
          const status = await getConversionStatus(token, doc.id);

          // Update progress
          setConversionProgress(prev => ({ ...prev, [doc.id]: status.progress }));

          // If done or failed, stop polling
          if (status.status === 'ready' || status.status === 'failed') {
            clearInterval(pollInterval);
            setConverting(prev => {
              const next = new Set(prev);
              next.delete(doc.id);
              return next;
            });

            // Reload documents to get updated conversion_status
            const docs = await getDocuments(token, setId, true);
            setDocuments(prev => ({ ...prev, [setId]: docs }));

            if (status.status === 'failed') {
              alert(`Conversion failed: ${status.error}`);
            }
          }
        } catch (error) {
          console.error('Failed to poll conversion status:', error);
          clearInterval(pollInterval);
          setConverting(prev => {
            const next = new Set(prev);
            next.delete(doc.id);
            return next;
          });
        }
      }, 1000); // Poll every second

    } catch (error) {
      console.error('Failed to start conversion:', error);
      alert('Failed to start conversion');
    }
  };

  const toggleDocumentVersions = async (doc: Document) => {
    if (doc.version <= 1) return;

    const newExpanded = new Set(expandedDocuments);
    if (newExpanded.has(doc.id)) {
      newExpanded.delete(doc.id);
      setExpandedDocuments(newExpanded);
    } else {
      newExpanded.add(doc.id);
      setExpandedDocuments(newExpanded);

      if (!documentVersions[doc.id] && token) {
        setLoadingVersions(prev => new Set(prev).add(doc.id));
        try {
          const versions = await getDocumentVersions(token, doc.id);
          setDocumentVersions(prev => ({ ...prev, [doc.id]: versions }));
        } catch (error) {
          console.error('Failed to load document versions:', error);
        } finally {
          setLoadingVersions(prev => {
            const next = new Set(prev);
            next.delete(doc.id);
            return next;
          });
        }
      }
    }
  };

  // Attachment handlers
  const handleAttachmentUpload = async () => {
    if (!token) return;
    const input = document.createElement('input');
    input.type = 'file';
    input.multiple = true;
    input.onchange = async () => {
      const files = input.files;
      if (!files || files.length === 0) return;
      setUploadingAttachments(true);
      try {
        await uploadAttachments(token, projectId, Array.from(files));
        const updated = await getProjectAttachments(token, projectId);
        setAttachments(updated);
        const mapNow = window.confirm('Files uploaded successfully. Map to rooms now?');
        if (mapNow) {
          setAttachmentsExpanded(true);
        }
      } catch (error) {
        console.error('Failed to upload attachments:', error);
        alert('Failed to upload attachments');
      } finally {
        setUploadingAttachments(false);
      }
    };
    input.click();
  };

  const handleRenameAttachment = async (attachment: Attachment) => {
    if (!token || !editingAttachmentName.trim()) return;
    try {
      const updated = await updateAttachment(token, attachment.id, editingAttachmentName);
      setAttachments(prev => prev.map(a => a.id === attachment.id ? updated : a));
      setEditingAttachmentId(null);
      setEditingAttachmentName('');
    } catch (error) {
      console.error('Failed to rename attachment:', error);
      alert('Failed to rename attachment');
    }
  };

  const handleDeleteAttachment = async (attachment: Attachment) => {
    if (!token) return;
    if (!window.confirm(`Are you sure you want to delete "${attachment.display_name}"? This cannot be undone.`)) return;
    try {
      await deleteAttachment(token, attachment.id);
      setAttachments(prev => prev.filter(a => a.id !== attachment.id));
    } catch (error) {
      console.error('Failed to delete attachment:', error);
      alert('Failed to delete attachment');
    }
  };

  const handleAddRoomToAttachment = async (attachmentId: number, roomId: number) => {
    if (!token) return;
    const attachment = attachments.find(a => a.id === attachmentId);
    if (!attachment) return;
    const currentRoomIds = attachment.room_mappings.map(m => m.room_id);
    if (currentRoomIds.includes(roomId)) return;
    // Optimistic update so dropdown stays open
    const optimistic = {
      ...attachment,
      room_mappings: [...attachment.room_mappings, { id: -Date.now(), room_id: roomId }]
    };
    setAttachments(prev => prev.map(a => a.id === attachmentId ? optimistic : a));
    try {
      const updated = await setAttachmentRooms(token, attachmentId, [...currentRoomIds, roomId]);
      setAttachments(prev => prev.map(a => a.id === attachmentId ? updated : a));
    } catch (error) {
      console.error('Failed to add room:', error);
      // Revert on failure
      setAttachments(prev => prev.map(a => a.id === attachmentId ? attachment : a));
    }
  };

  const handleRemoveRoomFromAttachment = async (attachmentId: number, roomId: number) => {
    if (!token) return;
    const attachment = attachments.find(a => a.id === attachmentId);
    if (!attachment) return;
    const newRoomIds = attachment.room_mappings.map(m => m.room_id).filter(id => id !== roomId);
    try {
      const updated = await setAttachmentRooms(token, attachmentId, newRoomIds);
      setAttachments(prev => prev.map(a => a.id === attachmentId ? updated : a));
    } catch (error) {
      console.error('Failed to remove room:', error);
    }
  };

  const getRelativeTime = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffSecs = Math.floor(diffMs / 1000);
    const diffMins = Math.floor(diffSecs / 60);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays > 30) return date.toLocaleDateString();
    if (diffDays > 0) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
    if (diffHours > 0) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    if (diffMins > 0) return `${diffMins} min${diffMins !== 1 ? 's' : ''} ago`;
    return 'just now';
  };

  const getFileIcon = (fileType: string) => {
    const lower = fileType.toLowerCase();
    if (['jpg', 'jpeg', 'png', 'webp', 'gif', 'bmp', 'svg'].includes(lower)) return null; // will use thumbnail
    if (lower === 'pdf') return '\u{1F4C4}';
    if (['xlsx', 'xls', 'csv'].includes(lower)) return '\u{1F4CA}';
    return '\u{1F4C1}';
  };

  const isImageType = (fileType: string) => {
    return ['jpg', 'jpeg', 'png', 'webp', 'gif', 'bmp', 'svg'].includes(fileType.toLowerCase());
  };

  const getFilteredRoomsForAttachment = (attachmentId: number) => {
    const attachment = attachments.find(a => a.id === attachmentId);
    const mappedIds = attachment ? attachment.room_mappings.map(m => m.room_id) : [];
    const search = roomSearchInput.toLowerCase();
    return rooms.filter(r => {
      if (mappedIds.includes(r.id)) return false;
      if (!search) return true;
      return (
        r.number.toLowerCase().includes(search) ||
        (r.name && r.name.toLowerCase().includes(search))
      );
    });
  };

  const getRoomLabel = (roomId: number) => {
    const room = rooms.find(r => r.id === roomId);
    if (!room) return `Room #${roomId}`;
    return room.name ? `${room.number} - ${room.name}` : room.number;
  };

  if (loading) {
    return <div>Loading document sets...</div>;
  }

  return (
    <div>
      {/* Document Sets Section */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <h2 style={{ margin: 0 }}>Documents</h2>
        <button
          onClick={() => setShowForm(!showForm)}
          style={{
            background: '#1a1a2e',
            color: '#fff',
            border: 'none',
            padding: '10px 20px',
            cursor: 'pointer',
            borderRadius: '4px'
          }}
        >
          {showForm ? 'Cancel' : '+ New Document Set'}
        </button>
      </div>

      {showForm && (
        <form onSubmit={handleCreateSet} style={{
          background: '#f5f5f5',
          padding: '20px',
          marginBottom: '24px',
          borderRadius: '8px'
        }}>
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold' }}>
              Document Set Name *
            </label>
            <input
              type="text"
              value={newSetName}
              onChange={(e) => setNewSetName(e.target.value)}
              placeholder="e.g., Floor Plans, Specifications"
              style={{ width: '100%', padding: '10px', boxSizing: 'border-box' }}
              required
            />
          </div>
          <button
            type="submit"
            style={{
              background: '#28a745',
              color: '#fff',
              border: 'none',
              padding: '10px 20px',
              cursor: 'pointer',
              borderRadius: '4px'
            }}
          >
            Create Document Set
          </button>
        </form>
      )}

      {documentSets.length === 0 ? (
        <p style={{ color: '#666' }}>No document sets yet. Create your first document set!</p>
      ) : (
        <div style={{ display: 'grid', gap: '12px' }}>
          {documentSets.map(set => {
            const isExpanded = expandedSets.has(set.id);
            const setDocuments = documents[set.id] || [];
            const isUploading = uploading === set.id;

            return (
              <div
                key={set.id}
                style={{
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  background: '#fff'
                }}
              >
                <div
                  style={{
                    padding: '16px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    cursor: 'pointer'
                  }}
                  onClick={() => toggleSet(set.id)}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <span style={{ fontSize: '18px' }}>
                      {isExpanded ? '\u25BC' : '\u25B6'}
                    </span>
                    <div>
                      <h4 style={{ margin: '0 0 4px 0', fontSize: '16px' }}>
                        {set.name}
                      </h4>
                      <p style={{ margin: 0, color: '#666', fontSize: '14px' }}>
                        {setDocuments.length} document{setDocuments.length !== 1 ? 's' : ''}
                      </p>
                    </div>
                  </div>
                </div>

                {isExpanded && (
                  <div style={{
                    padding: '0 16px 16px 16px',
                    borderTop: '1px solid #eee'
                  }}>
                    <div style={{ marginTop: '16px', marginBottom: '16px' }}>
                      {isUploading ? (
                      <div style={{
                        width: '160px',
                        height: '40px',
                        background: '#e0e0e0',
                        borderRadius: '4px',
                        overflow: 'hidden',
                        position: 'relative'
                      }}>
                        <div style={{
                          width: `${uploadProgress[set.id] || 0}%`,
                          height: '100%',
                          background: '#007bff',
                          transition: 'width 0.2s ease',
                          borderRadius: '4px'
                        }} />
                        <span style={{
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                          color: (uploadProgress[set.id] || 0) > 50 ? '#fff' : '#333',
                          fontWeight: 'bold',
                          fontSize: '12px'
                        }}>
                          {uploadProgress[set.id] || 0}%
                        </span>
                      </div>
                    ) : (
                      <label
                        htmlFor={`file-upload-${set.id}`}
                        style={{
                          display: 'inline-block',
                          background: '#007bff',
                          color: '#fff',
                          padding: '10px 20px',
                          cursor: 'pointer',
                          borderRadius: '4px'
                        }}
                      >
                        + Upload PDF
                      </label>
                    )}
                    <input
                      id={`file-upload-${set.id}`}
                      type="file"
                      accept=".pdf"
                      style={{ display: 'none' }}
                      disabled={isUploading}
                      onChange={(e) => handleFileSelect(set.id, e.target.files?.[0] || null)}
                      onClick={(e) => {
                        // Reset value to allow re-uploading the same file
                        (e.target as HTMLInputElement).value = '';
                      }}
                    />
                    </div>

                    {setDocuments.length === 0 ? (
                      <p style={{ color: '#666', fontStyle: 'italic' }}>
                        No documents uploaded yet.
                      </p>
                    ) : (
                      <div style={{ display: 'grid', gap: '8px' }}>
                        {setDocuments.filter(doc => doc.is_current).map(doc => (
                          <div key={doc.id}>
                            <div
                              style={{
                                border: '1px solid #e0e0e0',
                                borderRadius: '4px',
                                padding: '12px',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                background: '#fafafa'
                              }}
                            >
                              <div style={{ flex: 1 }}>
                                {editingDocId === doc.id ? (
                                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                    <input
                                      type="text"
                                      value={editingDocName}
                                      onChange={(e) => setEditingDocName(e.target.value)}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter') handleRenameDocument(doc, set.id);
                                        if (e.key === 'Escape') cancelEditing();
                                      }}
                                      style={{ flex: 1, padding: '6px 10px', fontSize: '14px' }}
                                      autoFocus
                                    />
                                    <button
                                      onClick={() => handleRenameDocument(doc, set.id)}
                                      style={{
                                        background: '#28a745',
                                        color: '#fff',
                                        border: 'none',
                                        padding: '6px 12px',
                                        cursor: 'pointer',
                                        borderRadius: '4px',
                                        fontSize: '12px'
                                      }}
                                    >
                                      Save
                                    </button>
                                    <button
                                      onClick={cancelEditing}
                                      style={{
                                        background: '#6c757d',
                                        color: '#fff',
                                        border: 'none',
                                        padding: '6px 12px',
                                        cursor: 'pointer',
                                        borderRadius: '4px',
                                        fontSize: '12px'
                                      }}
                                    >
                                      Cancel
                                    </button>
                                  </div>
                                ) : (
                                  <>
                                    <div
                                      style={{ fontWeight: 'bold', marginBottom: '4px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}
                                      onClick={() => startEditing(doc)}
                                      title="Click to rename"
                                    >
                                      {doc.filename}
                                      {doc.version > 1 && (
                                        <span
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            toggleDocumentVersions(doc);
                                          }}
                                          style={{
                                            background: expandedDocuments.has(doc.id) ? '#495057' : '#6c757d',
                                            color: '#fff',
                                            padding: '2px 8px',
                                            borderRadius: '10px',
                                            fontSize: '11px',
                                            fontWeight: 'normal',
                                            cursor: 'pointer',
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: '4px',
                                            transition: 'background 0.15s'
                                          }}
                                          title="Click to view previous versions"
                                        >
                                          <span style={{
                                            fontSize: '9px',
                                            transform: expandedDocuments.has(doc.id) ? 'rotate(90deg)' : 'rotate(0deg)',
                                            transition: 'transform 0.15s'
                                          }}>
                                            ▶
                                          </span>
                                          v{doc.version}
                                        </span>
                                      )}
                                    </div>
                                    <div style={{ fontSize: '14px', color: '#666' }}>
                                      {doc.page_count} page{doc.page_count !== 1 ? 's' : ''} ({doc.mapped_pages_count || 0} mapped)
                                      {doc.conversion_status === 'ready' && (
                                        <span style={{
                                          marginLeft: '8px',
                                          color: '#28a745',
                                          fontWeight: 'bold'
                                        }}>
                                          ✓ Web Friendly!
                                        </span>
                                      )}
                                    </div>

                                    {doc.conversion_status === 'ready' && (
                                      <OCRWarningsSection 
                                        documentId={doc.id}
                                        warningsJson={doc.conversion_warnings || null}
                                        conversionStatus={doc.conversion_status}
                                      />
                                    )}

                                    {/* Conversion status */}
                                    {doc.file_type === 'pdf' && doc.conversion_status === 'pending' && (
                                      <button
                                        onClick={() => handleConvertDocument(doc, set.id)}
                                        style={{
                                          marginTop: '8px',
                                          background: '#17a2b8',
                                          color: '#fff',
                                          border: 'none',
                                          padding: '6px 12px',
                                          cursor: 'pointer',
                                          borderRadius: '4px',
                                          fontSize: '12px'
                                        }}
                                      >
                                        Convert to Web Friendly
                                      </button>
                                    )}

                                    {/* Conversion progress */}
                                    {converting.has(doc.id) && (
                                      <div style={{ marginTop: '8px' }}>
                                        <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>
                                          Converting... Page {conversionProgress[doc.id] || 1} of {doc.page_count}
                                        </div>
                                        <div style={{
                                          width: '100%',
                                          height: '8px',
                                          background: '#e0e0e0',
                                          borderRadius: '4px',
                                          overflow: 'hidden'
                                        }}>
                                          <div style={{
                                            width: `${doc.page_count > 0 ? ((conversionProgress[doc.id] || 0) / doc.page_count) * 100 : 0}%`,
                                            height: '100%',
                                            background: '#17a2b8',
                                            transition: 'width 0.3s ease'
                                          }} />
                                        </div>
                                      </div>
                                    )}

                                    {doc.conversion_status === 'failed' && (
                                      <div style={{
                                        marginTop: '8px',
                                        padding: '8px',
                                        background: '#f8d7da',
                                        border: '1px solid #f5c6cb',
                                        borderRadius: '4px',
                                        fontSize: '12px',
                                        color: '#721c24'
                                      }}>
                                        Conversion failed: {doc.conversion_error}
                                      </div>
                                    )}
                                  </>
                                )}
                              </div>
                              {editingDocId !== doc.id && (
                                <div style={{ display: 'flex', gap: '8px' }}>
                                  <button
                                    onClick={() => startEditing(doc)}
                                    style={{
                                      background: '#6c757d',
                                      color: '#fff',
                                      border: 'none',
                                      padding: '8px 12px',
                                      cursor: 'pointer',
                                      borderRadius: '4px',
                                      fontSize: '12px'
                                    }}
                                  >
                                    Rename
                                  </button>
                                  <button
                                    onClick={() => navigate(`/admin/projects/${projectId}/documents/${doc.id}/map`)}
                                    style={{
                                      background: '#28a745',
                                      color: '#fff',
                                      border: 'none',
                                      padding: '8px 16px',
                                      cursor: 'pointer',
                                      borderRadius: '4px'
                                    }}
                                  >
                                    Map Rooms
                                  </button>
                                  <button
                                    onClick={() => handleDeleteDocument(doc, set.id)}
                                    style={{
                                      background: '#dc3545',
                                      color: '#fff',
                                      border: 'none',
                                      padding: '8px 12px',
                                      cursor: 'pointer',
                                      borderRadius: '4px',
                                      fontSize: '12px'
                                    }}
                                  >
                                    Delete
                                  </button>
                                </div>
                              )}
                            </div>

                            {expandedDocuments.has(doc.id) && (
                              <div style={{
                                marginTop: '8px',
                                marginLeft: '24px',
                                borderLeft: '2px solid #dee2e6'
                              }}>
                                {loadingVersions.has(doc.id) ? (
                                  <div style={{
                                    padding: '12px 16px',
                                    color: '#666',
                                    fontSize: '13px',
                                    fontStyle: 'italic'
                                  }}>
                                    Loading versions...
                                  </div>
                                ) : documentVersions[doc.id]?.filter(v => v.id !== doc.id).length === 0 ? (
                                  <div style={{
                                    padding: '12px 16px',
                                    color: '#666',
                                    fontSize: '13px'
                                  }}>
                                    No previous versions found.
                                  </div>
                                ) : (
                                  documentVersions[doc.id]
                                    ?.filter(v => v.id !== doc.id)
                                    .map(version => (
                                      <div
                                        key={version.id}
                                        style={{
                                          padding: '10px 16px',
                                          borderBottom: '1px solid #f0f0f0',
                                          background: '#f8f9fa',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'space-between',
                                          gap: '12px'
                                        }}
                                      >
                                        <div style={{ flex: 1 }}>
                                          <div style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '8px',
                                            marginBottom: '4px'
                                          }}>
                                            <span style={{
                                              background: '#adb5bd',
                                              color: '#fff',
                                              padding: '2px 6px',
                                              borderRadius: '8px',
                                              fontSize: '10px',
                                              fontWeight: 'bold'
                                            }}>
                                              v{version.version}
                                            </span>
                                            <span style={{ fontSize: '13px', color: '#495057' }}>
                                              {version.page_count} page{version.page_count !== 1 ? 's' : ''}
                                            </span>
                                          </div>
                                          <div style={{
                                            fontSize: '12px',
                                            color: '#868e96'
                                          }}>
                                            Uploaded {new Date(version.created_at).toLocaleDateString('en-US', {
                                              month: 'short',
                                              day: 'numeric',
                                              year: 'numeric',
                                              hour: 'numeric',
                                              minute: '2-digit'
                                            })}
                                            {version.uploader_id && (
                                              <span> by User #{version.uploader_id}</span>
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                    ))
                                )}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Attachments Section */}
      <div style={{ marginTop: '32px' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '16px',
            border: '1px solid #ddd',
            borderRadius: '8px',
            background: '#fff',
            cursor: 'pointer',
            marginBottom: attachmentsExpanded ? '0' : undefined,
            borderBottomLeftRadius: attachmentsExpanded ? '0' : '8px',
            borderBottomRightRadius: attachmentsExpanded ? '0' : '8px'
          }}
        >
          <div
            style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}
            onClick={() => setAttachmentsExpanded(!attachmentsExpanded)}
          >
            <span style={{ fontSize: '18px' }}>
              {attachmentsExpanded ? '\u25BC' : '\u25B6'}
            </span>
            <h4 style={{ margin: 0, fontSize: '16px' }}>
              Attachments ({attachments.length})
            </h4>
          </div>
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleAttachmentUpload();
            }}
            disabled={uploadingAttachments}
            style={{
              background: '#007bff',
              color: '#fff',
              border: 'none',
              padding: '8px 16px',
              cursor: uploadingAttachments ? 'not-allowed' : 'pointer',
              borderRadius: '4px',
              fontSize: '13px',
              opacity: uploadingAttachments ? 0.6 : 1
            }}
          >
            {uploadingAttachments ? 'Uploading...' : 'Upload'}
          </button>
        </div>

        {attachmentsExpanded && (
          <div style={{
            border: '1px solid #ddd',
            borderTop: 'none',
            borderBottomLeftRadius: '8px',
            borderBottomRightRadius: '8px',
            background: '#fff',
            padding: '16px'
          }}>
            {attachments.length === 0 ? (
              <p style={{ color: '#666', fontStyle: 'italic', margin: 0 }}>
                No attachments yet. Upload files to get started.
              </p>
            ) : (
              <div style={{ display: 'grid', gap: '8px' }}>
                {attachments.map(attachment => (
                  <div
                    key={attachment.id}
                    style={{
                      border: '1px solid #e0e0e0',
                      borderRadius: '4px',
                      padding: '12px',
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '12px',
                      background: '#fafafa'
                    }}
                  >
                    {/* Thumbnail / Icon */}
                    <div style={{
                      width: '48px',
                      height: '48px',
                      flexShrink: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '4px',
                      overflow: 'hidden',
                      background: '#e9ecef'
                    }}>
                      {isImageType(attachment.file_type) ? (
                        <img
                          src={getAttachmentDownloadUrl(attachment.id)}
                          alt={attachment.display_name}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover'
                          }}
                        />
                      ) : (
                        <span style={{ fontSize: '24px' }}>
                          {getFileIcon(attachment.file_type)}
                        </span>
                      )}
                    </div>

                    {/* Content */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      {/* Display name */}
                      {editingAttachmentId === attachment.id ? (
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '6px' }}>
                          <input
                            type="text"
                            value={editingAttachmentName}
                            onChange={(e) => setEditingAttachmentName(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') handleRenameAttachment(attachment);
                              if (e.key === 'Escape') { setEditingAttachmentId(null); setEditingAttachmentName(''); }
                            }}
                            onBlur={() => handleRenameAttachment(attachment)}
                            style={{
                              flex: 1,
                              padding: '4px 8px',
                              fontSize: '14px',
                              fontWeight: 'bold',
                              border: '1px solid #007bff',
                              borderRadius: '4px',
                              outline: 'none'
                            }}
                            autoFocus
                          />
                        </div>
                      ) : (
                        <div
                          style={{
                            fontWeight: 'bold',
                            fontSize: '14px',
                            marginBottom: '4px',
                            cursor: 'pointer',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                          onClick={() => {
                            setEditingAttachmentId(attachment.id);
                            setEditingAttachmentName(attachment.display_name);
                          }}
                          title="Click to rename"
                        >
                          {attachment.display_name}
                        </div>
                      )}

                      {/* Room chips */}
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', alignItems: 'center', marginBottom: '4px' }}>
                        {attachment.room_mappings.map(mapping => (
                          <span
                            key={mapping.room_id}
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px',
                              background: '#e3f2fd',
                              color: '#1565c0',
                              padding: '2px 8px',
                              borderRadius: '12px',
                              fontSize: '12px',
                              fontFamily: 'system-ui'
                            }}
                          >
                            {getRoomLabel(mapping.room_id)}
                            <span
                              onClick={() => handleRemoveRoomFromAttachment(attachment.id, mapping.room_id)}
                              style={{
                                cursor: 'pointer',
                                fontWeight: 'bold',
                                fontSize: '14px',
                                lineHeight: 1,
                                color: '#1565c0',
                                marginLeft: '2px'
                              }}
                              title="Remove room"
                            >
                              &times;
                            </span>
                          </span>
                        ))}
                        <span
                          onClick={() => {
                            setRoomDropdownAttachmentId(
                              roomDropdownAttachmentId === attachment.id ? null : attachment.id
                            );
                            setRoomSearchInput('');
                          }}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '22px',
                            height: '22px',
                            borderRadius: '50%',
                            border: '1px dashed #999',
                            cursor: 'pointer',
                            fontSize: '14px',
                            color: '#666',
                            lineHeight: 1
                          }}
                          title="Add room"
                        >
                          +
                        </span>
                      </div>

                      {/* Room dropdown */}
                      {roomDropdownAttachmentId === attachment.id && (
                        <div style={{
                          position: 'relative',
                          marginBottom: '6px'
                        }}>
                          <div style={{
                            border: '1px solid #ddd',
                            borderRadius: '6px',
                            background: '#fff',
                            boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
                            maxHeight: '200px',
                            overflow: 'auto',
                            zIndex: 10
                          }}>
                            <div style={{ padding: '8px', borderBottom: '1px solid #eee', position: 'sticky', top: 0, background: '#fff' }}>
                              <input
                                type="text"
                                value={roomSearchInput}
                                onChange={(e) => {
                                  setRoomSearchInput(e.target.value);
                                  setRoomHighlightIndex(0);
                                }}
                                onKeyDown={(e) => {
                                  const filtered = getFilteredRoomsForAttachment(attachment.id);
                                  if (e.key === 'ArrowDown') {
                                    e.preventDefault();
                                    setRoomHighlightIndex(prev => Math.min(prev + 1, filtered.length - 1));
                                  } else if (e.key === 'ArrowUp') {
                                    e.preventDefault();
                                    setRoomHighlightIndex(prev => Math.max(prev - 1, 0));
                                  } else if (e.key === 'Enter') {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    if (filtered.length > 0) {
                                      const idx = Math.min(roomHighlightIndex, filtered.length - 1);
                                      handleAddRoomToAttachment(attachment.id, filtered[idx].id);
                                      setRoomSearchInput('');
                                      setRoomHighlightIndex(0);
                                    }
                                  } else if (e.key === 'Escape') {
                                    e.stopPropagation();
                                    setRoomDropdownAttachmentId(null);
                                    setRoomSearchInput('');
                                    setRoomHighlightIndex(0);
                                  }
                                }}
                                placeholder="Search rooms..."
                                style={{
                                  width: '100%',
                                  padding: '6px 8px',
                                  border: '1px solid #ddd',
                                  borderRadius: '4px',
                                  fontSize: '13px',
                                  boxSizing: 'border-box',
                                  outline: 'none'
                                }}
                                autoFocus
                              />
                            </div>
                            {(() => {
                              const filtered = getFilteredRoomsForAttachment(attachment.id);
                              if (filtered.length === 0) {
                                return (
                                  <div style={{ padding: '12px', color: '#666', fontSize: '13px', textAlign: 'center' }}>
                                    No rooms found
                                  </div>
                                );
                              }
                              // Group by story
                              const grouped: Record<string, Room[]> = {};
                              filtered.forEach(room => {
                                const story = stories.find(s => s.id === room.story_id);
                                const groupName = story ? story.name : 'Unassigned';
                                if (!grouped[groupName]) grouped[groupName] = [];
                                grouped[groupName].push(room);
                              });
                              let flatIndex = 0;
                              return Object.entries(grouped).map(([groupName, groupRooms]) => (
                                <div key={groupName}>
                                  <div style={{
                                    padding: '6px 12px',
                                    fontSize: '11px',
                                    fontWeight: 'bold',
                                    color: '#666',
                                    textTransform: 'uppercase',
                                    background: '#f8f9fa'
                                  }}>
                                    {groupName}
                                  </div>
                                  {groupRooms.map(room => {
                                    const currentIndex = flatIndex++;
                                    const isHighlighted = currentIndex === roomHighlightIndex;
                                    return (
                                      <div
                                        key={room.id}
                                        onClick={() => {
                                          handleAddRoomToAttachment(attachment.id, room.id);
                                          setRoomSearchInput('');
                                          setRoomHighlightIndex(0);
                                        }}
                                        onMouseEnter={() => setRoomHighlightIndex(currentIndex)}
                                        style={{
                                          padding: '8px 12px',
                                          cursor: 'pointer',
                                          fontSize: '13px',
                                          borderBottom: '1px solid #f0f0f0',
                                          background: isHighlighted ? '#e9ecef' : 'transparent'
                                        }}
                                      >
                                        <span style={{ fontWeight: 'bold' }}>{room.number}</span>
                                        {room.name && <span style={{ color: '#666', marginLeft: '8px' }}>{room.name}</span>}
                                      </div>
                                    );
                                  })}
                                </div>
                              ));
                            })()}
                          </div>
                        </div>
                      )}

                      {/* Upload date */}
                      <div style={{ fontSize: '12px', color: '#666' }}>
                        {getRelativeTime(attachment.uploaded_at)}
                      </div>
                    </div>

                    {/* Actions menu */}
                    <div style={{ position: 'relative', flexShrink: 0 }}>
                      <button
                        onClick={() => setAttachmentMenuOpen(attachmentMenuOpen === attachment.id ? null : attachment.id)}
                        style={{
                          background: 'transparent',
                          border: 'none',
                          cursor: 'pointer',
                          fontSize: '18px',
                          padding: '4px 8px',
                          color: '#666',
                          borderRadius: '4px'
                        }}
                        title="Actions"
                      >
                        &#8942;
                      </button>
                      {attachmentMenuOpen === attachment.id && (
                        <div style={{
                          position: 'absolute',
                          right: 0,
                          top: '100%',
                          background: '#fff',
                          border: '1px solid #ddd',
                          borderRadius: '6px',
                          boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
                          zIndex: 20,
                          minWidth: '130px',
                          overflow: 'hidden'
                        }}>
                          <div
                            onClick={() => {
                              setEditingAttachmentId(attachment.id);
                              setEditingAttachmentName(attachment.display_name);
                              setAttachmentMenuOpen(null);
                            }}
                            style={{
                              padding: '10px 14px',
                              cursor: 'pointer',
                              fontSize: '13px',
                              borderBottom: '1px solid #f0f0f0'
                            }}
                            onMouseEnter={(e) => { e.currentTarget.style.background = '#f5f5f5'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                          >
                            Rename
                          </div>
                          <a
                            href={getAttachmentDownloadUrl(attachment.id)}
                            download
                            onClick={() => setAttachmentMenuOpen(null)}
                            style={{
                              display: 'block',
                              padding: '10px 14px',
                              cursor: 'pointer',
                              fontSize: '13px',
                              borderBottom: '1px solid #f0f0f0',
                              textDecoration: 'none',
                              color: 'inherit'
                            }}
                            onMouseEnter={(e) => { e.currentTarget.style.background = '#f5f5f5'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                          >
                            Download
                          </a>
                          <div
                            onClick={() => {
                              setAttachmentMenuOpen(null);
                              handleDeleteAttachment(attachment);
                            }}
                            style={{
                              padding: '10px 14px',
                              cursor: 'pointer',
                              fontSize: '13px',
                              color: '#dc3545'
                            }}
                            onMouseEnter={(e) => { e.currentTarget.style.background = '#f5f5f5'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                          >
                            Delete
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Upload Modal */}
      {uploadModal?.isOpen && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: '#fff',
            borderRadius: '8px',
            padding: '24px',
            maxWidth: '500px',
            width: '90%',
            maxHeight: '80vh',
            overflow: 'auto'
          }}>
            {uploadModal.step === 'choose' && (
              <>
                <h3 style={{ margin: '0 0 16px 0' }}>Upload Document</h3>
                <p style={{ margin: '0 0 24px 0', color: '#666' }}>
                  This document set already contains drawings. Is "<strong>{uploadModal.file?.name}</strong>" an addition or a replacement?
                </p>
                <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                  <button
                    onClick={() => setUploadModal(null)}
                    style={{
                      background: '#6c757d',
                      color: '#fff',
                      border: 'none',
                      padding: '10px 20px',
                      cursor: 'pointer',
                      borderRadius: '4px'
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => handleUploadChoice(false)}
                    style={{
                      background: '#28a745',
                      color: '#fff',
                      border: 'none',
                      padding: '10px 20px',
                      cursor: 'pointer',
                      borderRadius: '4px'
                    }}
                  >
                    Addition (New Drawing)
                  </button>
                  <button
                    onClick={() => handleUploadChoice(true)}
                    style={{
                      background: '#007bff',
                      color: '#fff',
                      border: 'none',
                      padding: '10px 20px',
                      cursor: 'pointer',
                      borderRadius: '4px'
                    }}
                  >
                    Replacement (Updated Version)
                  </button>
                </div>
              </>
            )}

            {uploadModal.step === 'select-doc' && (
              <>
                <h3 style={{ margin: '0 0 16px 0' }}>Select Document to Replace</h3>
                <p style={{ margin: '0 0 16px 0', color: '#666' }}>
                  Which drawing does "<strong>{uploadModal.file?.name}</strong>" replace?
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '16px' }}>
                  {(documents[uploadModal.setId] || [])
                    .filter(d => d.is_current)
                    .map(doc => (
                      <button
                        key={doc.id}
                        onClick={() => handleSelectReplacement(doc.id)}
                        style={{
                          background: '#f8f9fa',
                          border: '1px solid #ddd',
                          padding: '12px 16px',
                          cursor: 'pointer',
                          borderRadius: '4px',
                          textAlign: 'left',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = '#e9ecef';
                          e.currentTarget.style.borderColor = '#007bff';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = '#f8f9fa';
                          e.currentTarget.style.borderColor = '#ddd';
                        }}
                      >
                        <div>
                          <div style={{ fontWeight: 'bold' }}>{doc.filename}</div>
                          <div style={{ fontSize: '12px', color: '#666' }}>
                            {doc.page_count} page{doc.page_count !== 1 ? 's' : ''}
                            {doc.version > 1 && ` • v${doc.version}`}
                          </div>
                        </div>
                        <span style={{ color: '#007bff' }}>Select</span>
                      </button>
                    ))}
                </div>
                <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                  <button
                    onClick={() => setUploadModal(prev => prev ? { ...prev, step: 'choose' } : null)}
                    style={{
                      background: '#6c757d',
                      color: '#fff',
                      border: 'none',
                      padding: '10px 20px',
                      cursor: 'pointer',
                      borderRadius: '4px'
                    }}
                  >
                    Back
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

    </div>
  );
}

function TradesTab({ projectId }: { projectId: number }) {
  const { token } = useAuth();
  const [trades, setTrades] = useState<Trade[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [newTradeName, setNewTradeName] = useState('');

  const loadTrades = async () => {
    if (!token) return;
    try {
      const data = await getTradesAuth(token);
      setTrades(data);
    } catch (error) {
      console.error('Failed to load trades:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTrades();
  }, [token]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token || !newTradeName.trim()) return;
    try {
      await createTrade(token, newTradeName);
      setNewTradeName('');
      setShowForm(false);
      loadTrades();
    } catch (error) {
      console.error('Failed to create trade:', error);
    }
  };

  if (loading) {
    return <div>Loading trades...</div>;
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <h2 style={{ margin: 0 }}>Trades</h2>
        <button
          onClick={() => setShowForm(!showForm)}
          style={{
            background: '#1a1a2e',
            color: '#fff',
            border: 'none',
            padding: '10px 20px',
            cursor: 'pointer',
            borderRadius: '4px'
          }}
        >
          {showForm ? 'Cancel' : '+ New Trade'}
        </button>
      </div>

      {showForm && (
        <form onSubmit={handleCreate} style={{
          background: '#f5f5f5',
          padding: '20px',
          marginBottom: '24px',
          borderRadius: '8px'
        }}>
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold' }}>
              Trade Name *
            </label>
            <input
              type="text"
              value={newTradeName}
              onChange={(e) => setNewTradeName(e.target.value)}
              placeholder="e.g., Plumbing, Electrical, Carpentry"
              style={{ width: '100%', padding: '10px', boxSizing: 'border-box' }}
              required
            />
          </div>
          <button
            type="submit"
            style={{
              background: '#28a745',
              color: '#fff',
              border: 'none',
              padding: '10px 20px',
              cursor: 'pointer',
              borderRadius: '4px'
            }}
          >
            Create Trade
          </button>
        </form>
      )}

      {trades.length === 0 ? (
        <p style={{ color: '#666' }}>No trades yet. Create your first trade!</p>
      ) : (
        <div style={{ display: 'grid', gap: '12px' }}>
          {trades.map(trade => (
            <div
              key={trade.id}
              style={{
                border: '1px solid #ddd',
                borderRadius: '8px',
                padding: '16px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                background: '#fff'
              }}
            >
              <div>
                <h4 style={{ margin: 0, fontSize: '16px' }}>
                  {trade.name}
                </h4>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const ACTION_TYPE_LABELS: Record<string, string> = {
  'task_created': 'Task Created',
  'task_done': 'Task Completed',
  'task_verified': 'Task Verified',
  'note_added': 'Note Added'
};

const ACTION_TYPE_COLORS: Record<string, string> = {
  'task_created': '#17a2b8',
  'task_done': '#28a745',
  'task_verified': '#6f42c1',
  'note_added': '#fd7e14'
};

function ActivityTab({ projectId }: { projectId: number }) {
  const { token } = useAuth();
  const [activities, setActivities] = useState<ActivityLogType[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionTypeFilter, setActionTypeFilter] = useState<string>('');

  useEffect(() => {
    const loadActivity = async () => {
      if (!token) return;
      try {
        const activityData = await getProjectActivity(token, projectId, actionTypeFilter || undefined);
        setActivities(activityData);
      } catch (error) {
        console.error('Failed to load activity:', error);
      } finally {
        setLoading(false);
      }
    };

    loadActivity();
  }, [token, projectId, actionTypeFilter]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return <div>Loading activity...</div>;
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <h2 style={{ margin: 0 }}>Activity Log</h2>
      </div>

      {/* Filters */}
      <div style={{
        background: '#f8f9fa',
        padding: '20px',
        borderRadius: '8px',
        marginBottom: '24px'
      }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '16px',
          marginBottom: '16px'
        }}>
          <div>
            <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold', fontSize: '14px' }}>
              Filter by Action Type
            </label>
            <select
              value={actionTypeFilter}
              onChange={(e) => setActionTypeFilter(e.target.value)}
              style={{ width: '100%', padding: '8px', fontSize: '14px' }}
            >
              <option value="">All Actions</option>
              <option value="task_created">Task Created</option>
              <option value="task_done">Task Completed</option>
              <option value="task_verified">Task Verified</option>
              <option value="note_added">Note Added</option>
            </select>
          </div>
        </div>

        <div style={{
          padding: '10px 16px',
          background: '#fff',
          borderRadius: '4px',
          fontWeight: 'bold',
          display: 'inline-block'
        }}>
          {activities.length} activit{activities.length !== 1 ? 'ies' : 'y'} found
        </div>
      </div>

      {/* Activity Display */}
      {activities.length === 0 ? (
        <div style={{
          background: '#fff',
          padding: '40px',
          borderRadius: '8px',
          textAlign: 'center',
          color: '#666'
        }}>
          <p style={{ margin: 0, fontSize: '16px' }}>
            No activity found{actionTypeFilter ? ' for this action type' : ''}.
          </p>
        </div>
      ) : (
        <div style={{
          background: '#fff',
          borderRadius: '8px',
          overflow: 'hidden',
          border: '1px solid #ddd'
        }}>
          <table style={{
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: '14px'
          }}>
            <thead>
              <tr style={{ background: '#f8f9fa' }}>
                <th style={{
                  padding: '12px',
                  textAlign: 'left',
                  fontWeight: 'bold',
                  borderBottom: '2px solid #ddd',
                  width: '180px'
                }}>
                  When
                </th>
                <th style={{
                  padding: '12px',
                  textAlign: 'left',
                  fontWeight: 'bold',
                  borderBottom: '2px solid #ddd',
                  width: '140px'
                }}>
                  Action
                </th>
                <th style={{
                  padding: '12px',
                  textAlign: 'left',
                  fontWeight: 'bold',
                  borderBottom: '2px solid #ddd'
                }}>
                  Description
                </th>
              </tr>
            </thead>
            <tbody>
              {activities.map(activity => (
                <tr
                  key={activity.id}
                  style={{
                    borderBottom: '1px solid #eee'
                  }}
                >
                  <td style={{ padding: '12px', fontSize: '13px', color: '#666' }}>
                    {formatDate(activity.created_at)}
                  </td>
                  <td style={{ padding: '12px' }}>
                    <span style={{
                      display: 'inline-block',
                      padding: '4px 10px',
                      borderRadius: '12px',
                      fontSize: '12px',
                      fontWeight: 'bold',
                      color: '#fff',
                      background: ACTION_TYPE_COLORS[activity.action_type] || '#6c757d'
                    }}>
                      {ACTION_TYPE_LABELS[activity.action_type] || activity.action_type}
                    </span>
                  </td>
                  <td style={{ padding: '12px' }}>
                    <div style={{
                      maxWidth: '600px',
                      wordBreak: 'break-word'
                    }}>
                      {activity.description}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function NotesTab({ projectId }: { projectId: number }) {
  const { token, user } = useAuth();
  const isMobile = useMobileDetect();
  const [rooms, setRooms] = useState<Room[]>([]);
  const [notes, setNotes] = useState<NoteWithDetails[]>([]);
  const [loading, setLoading] = useState(true);
  const [roomFilter, setRoomFilter] = useState<number | null>(null);
  const [deletingNoteId, setDeletingNoteId] = useState<number | null>(null);

  // Edit modal state
  const [editingNote, setEditingNote] = useState<NoteWithDetails | null>(null);
  const [editContent, setEditContent] = useState('');
  const [editRoomIds, setEditRoomIds] = useState<number[]>([]);
  const [roomSearchInput, setRoomSearchInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [showRoomDropdown, setShowRoomDropdown] = useState(false);
  const [lightboxPhotoUrl, setLightboxPhotoUrl] = useState<string | null>(null);

  // Create modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newContent, setNewContent] = useState('');
  const [newRoomIds, setNewRoomIds] = useState<number[]>([]);
  const [newRoomSearchInput, setNewRoomSearchInput] = useState('');
  const [showNewRoomDropdown, setShowNewRoomDropdown] = useState(false);
  const [newNotePhoto, setNewNotePhoto] = useState<File | null>(null);
  const [newNotePhotoPreview, setNewNotePhotoPreview] = useState<string>('');

  useEffect(() => {
    const loadData = async () => {
      if (!token) return;
      try {
        const [notesData, roomsData] = await Promise.all([
          getProjectNotes(token, projectId),
          getRooms(token, projectId)
        ]);
        setNotes(notesData);
        setRooms(roomsData);
      } catch (error) {
        console.error('Failed to load notes data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [token, projectId]);

  const filteredNotes = notes.filter(note => {
    if (roomFilter !== null) {
      return note.rooms.some(r => r.room_id === roomFilter);
    }
    return true;
  });

  const handleDeleteNote = async (noteId: number) => {
    if (!token) return;
    if (!window.confirm('Are you sure you want to delete this note? This action cannot be undone.')) {
      return;
    }

    setDeletingNoteId(noteId);
    try {
      await deleteNote(token, noteId);
      setNotes(notes.filter(n => n.id !== noteId));
    } catch (error) {
      console.error('Failed to delete note:', error);
      alert('Failed to delete note');
    } finally {
      setDeletingNoteId(null);
    }
  };

  const handleEditClick = (note: NoteWithDetails) => {
    setEditingNote(note);
    setEditContent(note.content);
    setEditRoomIds(note.rooms.map(r => r.room_id));
    setRoomSearchInput('');
  };

  const handleSaveEdit = async () => {
    if (!token || !editingNote) return;
    if (!editContent.trim()) {
      alert('Note content cannot be empty');
      return;
    }
    if (editRoomIds.length === 0) {
      alert('At least one room is required');
      return;
    }

    setSaving(true);
    try {
      // Update content if changed
      if (editContent !== editingNote.content) {
        await updateNote(token, editingNote.id, editContent);
      }

      // Update rooms if changed
      const originalRoomIds = editingNote.rooms.map(r => r.room_id).sort();
      const newRoomIdsSorted = [...editRoomIds].sort();
      if (JSON.stringify(originalRoomIds) !== JSON.stringify(newRoomIdsSorted)) {
        await updateNoteRooms(token, editingNote.id, editRoomIds);
      }

      // Reload notes
      const notesData = await getProjectNotes(token, projectId);
      setNotes(notesData);
      setEditingNote(null);
    } catch (error) {
      console.error('Failed to update note:', error);
      alert('Failed to update note');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateNote = async () => {
    if (!token || !newContent.trim() || newRoomIds.length === 0) {
      alert('Please enter note content and select at least one room');
      return;
    }

    setSaving(true);
    try {
      const createdNote = await createMultiRoomNote(token, projectId, newContent, user?.username || 'Unknown', newRoomIds);
      
      if (newNotePhoto && createdNote.id) {
        try {
          await uploadNotePhoto(createdNote.id, newNotePhoto);
        } catch (photoError) {
          console.error('Failed to upload photo:', photoError);
          alert('Note created but photo upload failed');
        }
      }
      
      const notesData = await getProjectNotes(token, projectId);
      setNotes(notesData);
      setShowCreateModal(false);
      setNewContent('');
      setNewRoomIds([]);
      setNewNotePhoto(null);
      setNewNotePhotoPreview('');
    } catch (error) {
      console.error('Failed to create note:', error);
      alert('Failed to create note');
    } finally {
      setSaving(false);
    }
  };

  const getFilteredRoomsForSearch = (searchInput: string, excludeIds: number[]) => {
    if (!searchInput.trim()) return [];
    const search = searchInput.toLowerCase();
    return rooms
      .filter(r => !excludeIds.includes(r.id))
      .filter(r =>
        r.number.toLowerCase().includes(search) ||
        (r.name && r.name.toLowerCase().includes(search))
      )
      .slice(0, 10);
  };

  const addRoomToEdit = (roomId: number) => {
    if (!editRoomIds.includes(roomId)) {
      setEditRoomIds([...editRoomIds, roomId]);
    }
    setRoomSearchInput('');
    setShowRoomDropdown(false);
  };

  const removeRoomFromEdit = (roomId: number) => {
    setEditRoomIds(editRoomIds.filter(id => id !== roomId));
  };

  const addRoomToNew = (roomId: number) => {
    if (!newRoomIds.includes(roomId)) {
      setNewRoomIds([...newRoomIds, roomId]);
    }
    setNewRoomSearchInput('');
    setShowNewRoomDropdown(false);
  };

  const removeRoomFromNew = (roomId: number) => {
    setNewRoomIds(newRoomIds.filter(id => id !== roomId));
  };

  const handleNewNotePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        alert('Photo must be less than 10MB');
        return;
      }
      setNewNotePhoto(file);
      const reader = new FileReader();
      reader.onloadend = () => setNewNotePhotoPreview(reader.result as string);
      reader.readAsDataURL(file);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return <div>Loading notes...</div>;
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <h2 style={{ margin: 0 }}>Notes</h2>
        <button
          onClick={() => setShowCreateModal(true)}
          style={{
            background: '#007bff',
            color: '#fff',
            border: 'none',
            padding: '10px 20px',
            cursor: 'pointer',
            borderRadius: '4px',
            fontSize: '14px',
            fontWeight: 'bold'
          }}
        >
          + Add Note
        </button>
      </div>

      {/* Filters */}
      <div style={{
        background: '#f8f9fa',
        padding: '20px',
        borderRadius: '8px',
        marginBottom: '24px'
      }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '16px',
          marginBottom: '16px'
        }}>
          <div>
            <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold', fontSize: '14px' }}>
              Filter by Room
            </label>
            <select
              value={roomFilter === null ? '' : roomFilter}
              onChange={(e) => setRoomFilter(e.target.value ? parseInt(e.target.value) : null)}
              style={{ width: '100%', padding: '8px', fontSize: '14px' }}
            >
              <option value="">All Rooms</option>
              {rooms.map(room => (
                <option key={room.id} value={room.id}>
                  Room {room.number}{room.name ? ` - ${room.name}` : ''}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div style={{
          padding: '10px 16px',
          background: '#fff',
          borderRadius: '4px',
          fontWeight: 'bold',
          display: 'inline-block'
        }}>
          {filteredNotes.length} note{filteredNotes.length !== 1 ? 's' : ''} found
        </div>
      </div>

      {/* Notes Display */}
      {filteredNotes.length === 0 ? (
        <div style={{
          background: '#fff',
          padding: '40px',
          borderRadius: '8px',
          textAlign: 'center',
          color: '#666'
        }}>
          <p style={{ margin: 0, fontSize: '16px' }}>
            No notes found{roomFilter !== null ? ' for this room' : ''}.
          </p>
        </div>
      ) : isMobile ? (
        /* Mobile Card Layout */
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {filteredNotes.map(note => (
            <div
              key={note.id}
              onClick={() => handleEditClick(note)}
              onTouchStart={(e) => e.currentTarget.style.background = '#f5f5f5'}
              onTouchEnd={(e) => e.currentTarget.style.background = '#fff'}
              style={{
                background: '#fff',
                borderRadius: '8px',
                border: '1px solid #ddd',
                padding: '16px',
                cursor: 'pointer',
                transition: 'background 0.15s'
              }}
            >
              <div style={{ display: 'flex', flexWrap: 'wrap', marginBottom: '12px' }}>
                {note.rooms.map(room => (
                  <RoomChip key={room.room_id} room={room} />
                ))}
              </div>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', marginBottom: '12px' }}>
                {note.photo_url && (
                  <img
                    src={getNotePhotoUrl(note.photo_url) || ''}
                    alt="Note photo"
                    onClick={(e) => {
                      e.stopPropagation();
                      setLightboxPhotoUrl(getNotePhotoUrl(note.photo_url));
                    }}
                    style={{
                      width: '60px',
                      height: '60px',
                      objectFit: 'cover',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      flexShrink: 0
                    }}
                  />
                )}
                <div style={{
                  wordBreak: 'break-word',
                  whiteSpace: 'pre-wrap',
                  fontSize: '14px',
                  lineHeight: '1.5'
                }}>
                  {note.content}
                </div>
              </div>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                fontSize: '12px',
                color: '#666'
              }}>
                <span>{note.author_name}</span>
                <span>{formatDate(note.created_at)}</span>
              </div>
            </div>
          ))}
        </div>
      ) : (
        /* Desktop Table Layout */
        <div style={{
          background: '#fff',
          borderRadius: '8px',
          overflow: 'hidden',
          border: '1px solid #ddd'
        }}>
          <table style={{
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: '14px'
          }}>
            <thead>
              <tr style={{ background: '#f8f9fa' }}>
                <th style={{
                  padding: '12px',
                  textAlign: 'left',
                  fontWeight: 'bold',
                  borderBottom: '2px solid #ddd',
                  width: '180px'
                }}>
                  Rooms
                </th>
                <th style={{
                  padding: '12px',
                  textAlign: 'left',
                  fontWeight: 'bold',
                  borderBottom: '2px solid #ddd'
                }}>
                  Note Content
                </th>
                <th style={{
                  padding: '12px',
                  textAlign: 'left',
                  fontWeight: 'bold',
                  borderBottom: '2px solid #ddd',
                  width: '120px'
                }}>
                  Author
                </th>
                <th style={{
                  padding: '12px',
                  textAlign: 'left',
                  fontWeight: 'bold',
                  borderBottom: '2px solid #ddd',
                  width: '180px'
                }}>
                  Created
                </th>
                <th style={{
                  padding: '12px',
                  textAlign: 'center',
                  fontWeight: 'bold',
                  borderBottom: '2px solid #ddd',
                  width: '80px'
                }}>
                  Edit
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredNotes.map(note => (
                <tr
                  key={note.id}
                  style={{
                    borderBottom: '1px solid #eee'
                  }}
                >
                  <td style={{ padding: '12px' }}>
                    <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                      {note.rooms.map(room => (
                        <RoomChip key={room.room_id} room={room} />
                      ))}
                    </div>
                  </td>
                  <td style={{ padding: '12px' }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                      {note.photo_url && (
                        <img
                          src={getNotePhotoUrl(note.photo_url) || ''}
                          alt="Note photo"
                          onClick={(e) => {
                            e.stopPropagation();
                            setLightboxPhotoUrl(getNotePhotoUrl(note.photo_url));
                          }}
                          style={{
                            width: '60px',
                            height: '60px',
                            objectFit: 'cover',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            flexShrink: 0
                          }}
                        />
                      )}
                      <div style={{
                        maxWidth: '500px',
                        wordBreak: 'break-word',
                        whiteSpace: 'pre-wrap'
                      }}>
                        {note.content}
                      </div>
                    </div>
                  </td>
                  <td style={{ padding: '12px' }}>
                    {note.author_name}
                  </td>
                  <td style={{ padding: '12px', fontSize: '13px', color: '#666' }}>
                    {formatDate(note.created_at)}
                  </td>
                  <td style={{ padding: '12px', textAlign: 'center' }}>
                    <button
                      onClick={() => handleEditClick(note)}
                      style={{
                        background: '#007bff',
                        color: '#fff',
                        border: 'none',
                        padding: '6px 12px',
                        cursor: 'pointer',
                        borderRadius: '4px',
                        fontSize: '12px',
                        fontWeight: 'bold'
                      }}
                    >
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Edit Modal */}
      {editingNote && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: isMobile ? 'flex-end' : 'center',
          justifyContent: 'center',
          zIndex: 1000
        }} onClick={() => setEditingNote(null)}>
          <div style={{
            background: '#fff',
            padding: isMobile ? '20px' : '24px',
            borderRadius: isMobile ? '16px 16px 0 0' : '8px',
            width: isMobile ? '100%' : '500px',
            maxWidth: isMobile ? '100%' : '90%',
            maxHeight: isMobile ? '85vh' : '90vh',
            overflowY: 'auto'
          }} onClick={(e) => e.stopPropagation()}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px'
            }}>
              <h3 style={{ margin: 0 }}>Edit Note</h3>
              <button
                onClick={() => {
                  if (window.confirm('Are you sure you want to delete this note?')) {
                    handleDeleteNote(editingNote.id);
                    setEditingNote(null);
                  }
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#dc3545',
                  fontSize: '18px',
                  padding: '4px 8px'
                }}
                title="Delete note"
              >
                🗑️
              </button>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold', fontSize: '14px' }}>
                Content
              </label>
              <textarea
                value={editContent}
                onChange={(e) => setEditContent(e.target.value)}
                rows={5}
                style={{ width: '100%', padding: '8px', fontSize: '14px', boxSizing: 'border-box' }}
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <RoomSelector
                rooms={rooms}
                selectedRoomIds={editRoomIds}
                searchInput={roomSearchInput}
                setSearchInput={setRoomSearchInput}
                showDropdown={showRoomDropdown}
                setShowDropdown={setShowRoomDropdown}
                onAddRoom={addRoomToEdit}
                onRemoveRoom={removeRoomFromEdit}
                getFilteredRoomsForSearch={getFilteredRoomsForSearch}
              />
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              <button
                onClick={() => setEditingNote(null)}
                style={{
                  background: '#6c757d',
                  color: '#fff',
                  border: 'none',
                  padding: '10px 20px',
                  cursor: 'pointer',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveEdit}
                disabled={saving}
                style={{
                  background: '#007bff',
                  color: '#fff',
                  border: 'none',
                  padding: '10px 20px',
                  cursor: saving ? 'wait' : 'pointer',
                  borderRadius: '4px',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  opacity: saving ? 0.6 : 1
                }}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: isMobile ? 'flex-end' : 'center',
          justifyContent: 'center',
          zIndex: 1000
        }} onClick={() => setShowCreateModal(false)}>
          <div style={{
            background: '#fff',
            padding: isMobile ? '20px' : '24px',
            borderRadius: isMobile ? '16px 16px 0 0' : '8px',
            width: isMobile ? '100%' : '500px',
            maxWidth: isMobile ? '100%' : '90%',
            maxHeight: isMobile ? '85vh' : '90vh',
            overflowY: 'auto'
          }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 20px 0' }}>Add Note</h3>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold', fontSize: '14px' }}>
                Content
              </label>
              <textarea
                value={newContent}
                onChange={(e) => setNewContent(e.target.value)}
                rows={5}
                placeholder="Enter note content..."
                style={{ width: '100%', padding: '8px', fontSize: '14px', boxSizing: 'border-box' }}
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <RoomSelector
                rooms={rooms}
                selectedRoomIds={newRoomIds}
                searchInput={newRoomSearchInput}
                setSearchInput={setNewRoomSearchInput}
                showDropdown={showNewRoomDropdown}
                setShowDropdown={setShowNewRoomDropdown}
                onAddRoom={addRoomToNew}
                onRemoveRoom={removeRoomFromNew}
                getFilteredRoomsForSearch={getFilteredRoomsForSearch}
              />
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
                Photo (optional)
              </label>
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp,image/heic"
                onChange={handleNewNotePhotoChange}
                style={{ marginBottom: '0.5rem' }}
              />
              {newNotePhotoPreview && (
                <div style={{ position: 'relative', display: 'inline-block' }}>
                  <img
                    src={newNotePhotoPreview}
                    alt="Preview"
                    style={{ maxWidth: '200px', maxHeight: '150px', borderRadius: '4px' }}
                  />
                  <button
                    type="button"
                    onClick={() => { setNewNotePhoto(null); setNewNotePhotoPreview(''); }}
                    style={{
                      position: 'absolute',
                      top: '-8px',
                      right: '-8px',
                      background: '#dc3545',
                      color: 'white',
                      border: 'none',
                      borderRadius: '50%',
                      width: '24px',
                      height: '24px',
                      cursor: 'pointer',
                      fontSize: '14px'
                    }}
                  >
                    ×
                  </button>
                </div>
              )}
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setNewContent('');
                  setNewRoomIds([]);
                  setNewNotePhoto(null);
                  setNewNotePhotoPreview('');
                }}
                style={{
                  background: '#6c757d',
                  color: '#fff',
                  border: 'none',
                  padding: '10px 20px',
                  cursor: 'pointer',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleCreateNote}
                disabled={saving || !newContent.trim() || newRoomIds.length === 0}
                style={{
                  background: '#007bff',
                  color: '#fff',
                  border: 'none',
                  padding: '10px 20px',
                  cursor: saving ? 'wait' : 'pointer',
                  borderRadius: '4px',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  opacity: (saving || !newContent.trim() || newRoomIds.length === 0) ? 0.6 : 1
                }}
              >
                {saving ? 'Creating...' : 'Create Note'}
              </button>
            </div>
          </div>
        </div>
      )}

      <PhotoLightbox
        photoUrl={lightboxPhotoUrl}
        isOpen={lightboxPhotoUrl !== null}
        onClose={() => setLightboxPhotoUrl(null)}
      />
    </div>
  );
}

interface SettingsTabProps {
  projectId: number;
  project: Project | null;
  onProjectUpdated: (project: Project) => void;
}

function SettingsTab({ projectId, project, onProjectUpdated }: SettingsTabProps) {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [editName, setEditName] = useState(project?.name || '');
  const [editAddress, setEditAddress] = useState(project?.address || '');
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [trades, setTrades] = useState<Trade[]>([]);
  const [tradesLoading, setTradesLoading] = useState(true);
  const [showTradeForm, setShowTradeForm] = useState(false);
  const [newTradeName, setNewTradeName] = useState('');
  const [editingTradeId, setEditingTradeId] = useState<number | null>(null);
  const [editTradeName, setEditTradeName] = useState('');
  const [tradesExpanded, setTradesExpanded] = useState(false);

  useEffect(() => {
    if (project) {
      setEditName(project.name);
      setEditAddress(project.address || '');
    }
  }, [project]);

  const loadTrades = async () => {
    if (!token) return;
    setTradesLoading(true);
    try {
      const data = await getTradesAuth(token);
      setTrades(data);
    } catch (error) {
      console.error('Failed to load trades:', error);
    } finally {
      setTradesLoading(false);
    }
  };

  useEffect(() => {
    loadTrades();
  }, [token]);

  const handleCreateTrade = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token || !newTradeName.trim()) return;
    try {
      const trade = await createTrade(token, newTradeName.trim());
      setTrades([...trades, trade]);
      setNewTradeName('');
      setShowTradeForm(false);
    } catch (error) {
      console.error('Failed to create trade:', error);
      alert('Failed to create trade. Please try again.');
    }
  };

  const handleDeleteTrade = async (tradeId: number) => {
    if (!token) return;
    if (!window.confirm('Are you sure you want to delete this trade?')) return;
    try {
      await deleteTrade(token, tradeId);
      setTrades(trades.filter(t => t.id !== tradeId));
    } catch (error) {
      console.error('Failed to delete trade:', error);
      alert('Failed to delete trade. Please try again.');
    }
  };

  const handleUpdateTrade = async (tradeId: number) => {
    if (!token || !editTradeName.trim()) return;
    try {
      const updated = await updateTrade(token, tradeId, editTradeName.trim());
      setTrades(trades.map(t => t.id === tradeId ? updated : t));
      setEditingTradeId(null);
      setEditTradeName('');
    } catch (error) {
      console.error('Failed to update trade:', error);
      alert('Failed to update trade. Please try again.');
    }
  };

  const handleSave = async () => {
    if (!token || !editName.trim()) return;
    setSaving(true);
    try {
      const updated = await updateProject(token, projectId, {
        name: editName.trim(),
        address: editAddress.trim() || undefined
      });
      onProjectUpdated(updated);
      setIsEditing(false);
    } catch (error) {
      console.error('Failed to update project:', error);
      alert('Failed to update project. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setEditName(project?.name || '');
    setEditAddress(project?.address || '');
    setIsEditing(false);
  };

  const handleDelete = async () => {
    if (!token) return;
    setDeleting(true);
    try {
      await deleteProject(token, projectId);
      navigate('/admin');
    } catch (error) {
      console.error('Failed to delete project:', error);
      alert('Failed to delete project. Please try again.');
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  if (!project) {
    return <div>Loading...</div>;
  }

  return (
    <div>
      {/* Project Settings - moved to top */}
      <h2 style={{ margin: '0 0 24px 0' }}>Project Settings</h2>

      <div style={{
        background: '#f5f5f5',
        padding: '24px',
        borderRadius: '8px',
        marginBottom: '32px'
      }}>
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', fontSize: '14px' }}>
            Project Name *
          </label>
          <input
            type="text"
            value={editName}
            onChange={(e) => { setEditName(e.target.value); setIsEditing(true); }}
            placeholder="Enter project name"
            style={{
              width: '100%',
              padding: '12px',
              fontSize: '14px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              boxSizing: 'border-box'
            }}
            required
          />
        </div>

        <div style={{ marginBottom: '24px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', fontSize: '14px' }}>
            Address
          </label>
          <input
            type="text"
            value={editAddress}
            onChange={(e) => { setEditAddress(e.target.value); setIsEditing(true); }}
            placeholder="Enter project address (optional)"
            style={{
              width: '100%',
              padding: '12px',
              fontSize: '14px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              boxSizing: 'border-box'
            }}
          />
        </div>

        <div style={{ display: 'flex', gap: '12px' }}>
          <button
            onClick={handleSave}
            disabled={saving || !editName.trim() || !isEditing}
            style={{
              background: '#28a745',
              color: '#fff',
              border: 'none',
              padding: '12px 24px',
              cursor: (saving || !editName.trim() || !isEditing) ? 'not-allowed' : 'pointer',
              borderRadius: '4px',
              fontSize: '14px',
              fontWeight: 'bold',
              opacity: (saving || !editName.trim() || !isEditing) ? 0.6 : 1
            }}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
          {isEditing && (
            <button
              onClick={handleCancel}
              disabled={saving}
              style={{
                background: '#6c757d',
                color: '#fff',
                border: 'none',
                padding: '12px 24px',
                cursor: saving ? 'not-allowed' : 'pointer',
                borderRadius: '4px',
                fontSize: '14px'
              }}
            >
              Cancel
            </button>
          )}
        </div>
      </div>

      {/* Collapsible Trades Management */}
      <div style={{ marginBottom: '32px' }}>
        {/* Collapsible Header */}
        <div
          onClick={() => setTradesExpanded(!tradesExpanded)}
          style={{
            padding: '16px',
            background: '#f5f5f5',
            border: '1px solid #e0e0e0',
            borderRadius: tradesExpanded ? '4px 4px 0 0' : '4px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            userSelect: 'none'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{
              display: 'inline-block',
              transition: 'transform 0.3s ease',
              transform: tradesExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
              fontSize: '12px'
            }}>
              ▶
            </span>
            <h3 style={{ margin: 0, fontSize: '16px' }}>Trades Management</h3>
            <span style={{ color: '#666', fontSize: '14px' }}>
              ({trades.length} trade{trades.length !== 1 ? 's' : ''})
            </span>
          </div>
        </div>

        {/* Collapsible Content */}
        <div style={{
          maxHeight: tradesExpanded ? '1000px' : '0',
          overflow: 'hidden',
          transition: 'max-height 0.3s ease',
          background: '#fafafa',
          borderLeft: tradesExpanded ? '1px solid #e0e0e0' : 'none',
          borderRight: tradesExpanded ? '1px solid #e0e0e0' : 'none',
          borderBottom: tradesExpanded ? '1px solid #e0e0e0' : 'none',
          borderRadius: '0 0 4px 4px'
        }}>
          <div style={{ padding: '16px' }}>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '16px' }}>
              <button
                onClick={(e) => { e.stopPropagation(); setShowTradeForm(true); }}
                style={{
                  background: '#28a745',
                  color: '#fff',
                  border: 'none',
                  padding: '8px 16px',
                  cursor: 'pointer',
                  borderRadius: '4px',
                  fontSize: '14px',
                  fontWeight: 'bold'
                }}
              >
                + New Trade
              </button>
            </div>

            {showTradeForm && (
              <form onSubmit={handleCreateTrade} style={{ marginBottom: '16px' }}>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input
                    type="text"
                    value={newTradeName}
                    onChange={(e) => setNewTradeName(e.target.value)}
                    placeholder="Trade name"
                    style={{
                      flex: 1,
                      padding: '8px 12px',
                      fontSize: '14px',
                      border: '1px solid #ddd',
                      borderRadius: '4px'
                    }}
                    autoFocus
                  />
                  <button
                    type="submit"
                    disabled={!newTradeName.trim()}
                    style={{
                      background: '#28a745',
                      color: '#fff',
                      border: 'none',
                      padding: '8px 16px',
                      cursor: newTradeName.trim() ? 'pointer' : 'not-allowed',
                      borderRadius: '4px',
                      fontSize: '14px',
                      opacity: newTradeName.trim() ? 1 : 0.6
                    }}
                  >
                    Add
                  </button>
                  <button
                    type="button"
                    onClick={() => { setShowTradeForm(false); setNewTradeName(''); }}
                    style={{
                      background: '#6c757d',
                      color: '#fff',
                      border: 'none',
                      padding: '8px 16px',
                      cursor: 'pointer',
                      borderRadius: '4px',
                      fontSize: '14px'
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            )}

            {tradesLoading ? (
              <div style={{ color: '#666' }}>Loading trades...</div>
            ) : trades.length === 0 ? (
              <div style={{ color: '#666' }}>No trades yet. Click "+ New Trade" to add one.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {trades.map((trade) => (
                  <div key={trade.id} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {editingTradeId === trade.id ? (
                      <>
                        <input
                          type="text"
                          value={editTradeName}
                          onChange={(e) => setEditTradeName(e.target.value)}
                          style={{
                            flex: 1,
                            padding: '8px 12px',
                            fontSize: '14px',
                            border: '1px solid #ddd',
                            borderRadius: '4px'
                          }}
                          autoFocus
                        />
                        <button
                          onClick={() => handleUpdateTrade(trade.id)}
                          disabled={!editTradeName.trim()}
                          style={{
                            background: '#28a745',
                            color: '#fff',
                            border: 'none',
                            padding: '6px 12px',
                            cursor: editTradeName.trim() ? 'pointer' : 'not-allowed',
                            borderRadius: '4px',
                            fontSize: '13px',
                            opacity: editTradeName.trim() ? 1 : 0.6
                          }}
                        >
                          Save
                        </button>
                        <button
                          onClick={() => { setEditingTradeId(null); setEditTradeName(''); }}
                          style={{
                            background: '#6c757d',
                            color: '#fff',
                            border: 'none',
                            padding: '6px 12px',
                            cursor: 'pointer',
                            borderRadius: '4px',
                            fontSize: '13px'
                          }}
                        >
                          Cancel
                        </button>
                      </>
                    ) : (
                      <>
                        <span
                          onClick={() => { setEditingTradeId(trade.id); setEditTradeName(trade.name); }}
                          style={{
                            flex: 1,
                            padding: '8px 12px',
                            background: '#fff',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '14px'
                          }}
                        >
                          {trade.name}
                        </span>
                        <button
                          onClick={() => handleDeleteTrade(trade.id)}
                          style={{
                            background: 'transparent',
                            border: 'none',
                            color: '#999',
                            fontSize: '18px',
                            cursor: 'pointer',
                            padding: '4px 8px',
                            lineHeight: 1
                          }}
                          onMouseEnter={(e) => (e.currentTarget.style.color = '#dc3545')}
                          onMouseLeave={(e) => (e.currentTarget.style.color = '#999')}
                        >
                          ×
                        </button>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Danger Zone */}
      <div style={{
        border: '2px solid #dc3545',
        borderRadius: '8px',
        padding: '24px'
      }}>
        <h3 style={{ margin: '0 0 16px 0', color: '#dc3545' }}>Danger Zone</h3>
        <p style={{ margin: '0 0 16px 0', color: '#666', fontSize: '14px' }}>
          Deleting a project is permanent and cannot be undone. All rooms, documents, document sets, and tasks associated with this project will be permanently deleted.
        </p>
        <button
          onClick={() => setShowDeleteConfirm(true)}
          style={{
            background: '#dc3545',
            color: '#fff',
            border: 'none',
            padding: '12px 24px',
            cursor: 'pointer',
            borderRadius: '4px',
            fontSize: '14px',
            fontWeight: 'bold'
          }}
        >
          Delete Project
        </button>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: '#fff',
            borderRadius: '8px',
            padding: '24px',
            maxWidth: '500px',
            width: '90%'
          }}>
            <h3 style={{ margin: '0 0 16px 0', color: '#dc3545' }}>Delete Project?</h3>
            <p style={{ margin: '0 0 24px 0', color: '#333', fontSize: '14px', lineHeight: '1.6' }}>
              Are you sure you want to delete "<strong>{project.name}</strong>"? This will permanently delete all rooms, documents, document sets, and tasks associated with this project. This action cannot be undone.
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowDeleteConfirm(false)}
                disabled={deleting}
                style={{
                  background: '#6c757d',
                  color: '#fff',
                  border: 'none',
                  padding: '10px 20px',
                  cursor: deleting ? 'not-allowed' : 'pointer',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                style={{
                  background: '#dc3545',
                  color: '#fff',
                  border: 'none',
                  padding: '10px 20px',
                  cursor: deleting ? 'not-allowed' : 'pointer',
                  borderRadius: '4px',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  opacity: deleting ? 0.6 : 1
                }}
              >
                {deleting ? 'Deleting...' : 'Delete Project'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
